// @TODO: remove this and cleanup types
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import 'reflect-metadata'

import { Exclude, Type } from 'class-transformer'
import { orderBy } from 'lodash'
import { CardString, Hand } from 'pokersolver'
import { Subject, Subscriber } from 'rxjs'
import { v4 as nanoid } from 'uuid'

// import { BoardStateTag } from '../model/states/GameState'
import { BotKind } from '../model/BotKind'
import { canReceiveChips, getCardString } from '../util/Poker'
import { ColorsForPlayers, PlayerColors } from '../util/Colors'
import { defaultPlayerActionTimeQuickPlay, Timing } from '../util/Timing'
import { THREE_CARD_AMOUNT_OF_HANDS } from '../util/ThreeCard'
import { BetUndefinedForPlayer } from '../error/table/BetUndefinedForPlayer'
import { CannotStackPotsBeforeShowdown } from '../error/table/CannotStackPotsBeforeShowdown'
import { CantCallNoBetError } from '../error/table/CantCallNoBetError'
import { PlayOutOfTurnError } from '../error/table/PlayOutOfTurnError'
import { TriedToPlayBlindsOutOfOrderError } from '../error/table/TriedToPlayBlindsOutOfOrderError'
import { Language } from '../lang/Language'
import { TableEvent, TableEventKind, TableEvents } from '../messaging/event/TableEvent'
import { Evented } from '../messaging/EventSourced'
import { logger } from '../util/Logger'
import { ActionEvaluator } from './ActionEvaluator'
import { Bet, BetKind } from './Bet'
import { BettingRecommender, BettingValidator } from './BettingRecommender'
import { Blind } from './Blind'
import { Board } from './Board'
import { Player } from './Player'
import { PlayerKind } from './PlayerKind'
import { Pot, PotKind, PotWinner } from './Pot'
import { GameModes } from './states/GameMode'
import { GameMode } from './states/GameMode.types'
import { GameStates } from './states/GameState'
import { GameState } from './states/GameState.types'
import { GameActions } from './states/GameStateAction'
import { GameReducer } from './states/GameStateReducer'
import { PlayerStates } from './states/PlayerState'
import { TournamentDifficulty } from './TournamentDifficulty'

const isTestingWithJest = process.env.JEST_WORKER_ID !== undefined
const isProd = (process.env.STAGE || 'dev') === 'prod'

export const MAX_DO_NO_ACTION_COUNT = 2
export const INACTIVITY_DURATION_TO_KICK = 60 * 1000 * (isProd ? 15 : 3)

export const TABLE_CONFIG = {
  AUTO_START: !!isTestingWithJest,
  DEFAULT_BIG_BLIND: 100,
  TOURNAMENT_BIG_BLIND: 100,
  BLIND_LEVELS: [
    200, 300, 400, 500, 600, 800, 1000, 1200, 1400, 1600, 1800, 2000, 3000, 4000, 5000, 6000, 7000, 8000, 9000, 10000,
    11000, 12000, 14000
  ],
  MIN_PLAYERS: 2,
  MAX_PLAYERS: 9,
  MAX_DO_NO_ACTION_COUNT,
  INACTIVITY_DURATION_TO_KICK
}

const defaultPlayerActionTime = defaultPlayerActionTimeQuickPlay * 1000

type EventHandler = {
  name: string
  forPlayerId?: number
  withBet?: Bet
}

type AddPlayerOptions = {
  name: string
  chips: number
  userId?: string
  userRoles?: string[]
  canNotPlay?: boolean
  playerKind?: PlayerKind
  botKind?: BotKind
  isThreeCardGame?: boolean
  shouldCompleteEvents?: boolean
}

export type StreetAggressorEvent = {
  street: Board['state']['tag']
  playerId: string
  event: unknown
}

export class Table extends Evented {
  @Type(() => Board)
  board: Board
  @Type(() => Blind)
  blinds: Blind
  @Type(() => Blind)
  nextBlinds: Blind
  @Type(() => Blind)
  updatedCurrentBlinds: Blind
  @Type(() => Player)
  players: Player[] = []
  @Type(() => Player)
  leftPlayers: Player[] = []
  button = -1
  currentPlayer = 0
  previousPlayerId = ''
  buttonPlayerId = ''
  currentPlayerId = ''
  gameId = ''
  gameName = ''
  smallBlindLeft = false
  bigBlindLeft = false
  smallBlindFold = false
  bigBlindFold = false
  blindTimerCheckpoint = 0
  blindTimerCountdown = 5
  gameStartTime = 0
  gamePauseTime = 0
  amountOfHands = 0
  playerActionTimeLeft = defaultPlayerActionTime
  playerActionTime = defaultPlayerActionTime
  nextBlindIndex = 0
  isCardsVisibleByTeacher = false
  isCardsVisibleByEveryone = false
  isCardsVisibleOnShowDown = false
  boardDealState = ''
  removedPlayerId = ''
  isSmallOrBigBlindForcedAllIn = false
  isDeadSmallBlindCurrentHand = false
  isDeadDealerCurrentHand = false
  isDeadSmallBlindNextHand = false
  isTableFull = false
  startTimerCheckpoint = 0
  @Type(() => GameState)
  state: GameState
  isSleeping = false
  isGameOverForced = false
  @Type(() => GameModes)
  mode: GameMode
  difficulty: TournamentDifficulty
  tournamentJoinTime: number
  finalWinnerInfo: { communityCards: CardString[]; winner: PotWinner }
  threeCardDeck?: CardString[]

  streetAggressorEvent: StreetAggressorEvent[] = []

  @Exclude()
  @Type(() => Subject)
  public event$ = new Subject<TableEvent>()

  constructor(
    public name: string,
    id = nanoid(),
    public bigBlind = TABLE_CONFIG.DEFAULT_BIG_BLIND,
    public minPlayers = TABLE_CONFIG.MIN_PLAYERS,
    public maxPlayers = TABLE_CONFIG.MAX_PLAYERS,
    public autostart = TABLE_CONFIG.AUTO_START,
    public maxDoNoActionCount = TABLE_CONFIG.MAX_DO_NO_ACTION_COUNT,
    subscriber?: Subscriber<TableEvent>
  ) {
    super(id)

    this.state = GameStates.PreGame
    this.mode = GameModes.Default
    this.blinds = new Blind(bigBlind)
    this.updatedCurrentBlinds = new Blind(bigBlind)
    this.nextBlinds = new Blind(bigBlind * 2)
    this.board = new Board(this.blinds)
    this.difficulty = TournamentDifficulty.GENERAL

    if (subscriber) {
      this.event$.subscribe(subscriber)
    }

    const eventVersion = this.updateState({ name: TableEventKind.Created })
    this.emitEvent({ ...TableEvents.tableCreatedEvent(this), ...eventVersion })
  }

  setStreetAggressionEvent(data: StreetAggressorEvent) {
    try {
      this.streetAggressorEvent = [...this.streetAggressorEvent, data]
    } catch (error) {
      console.error('Table.setStreetAggressionEvent', error)
    }
  }

  completeEvents() {
    if (!isTestingWithJest) {
      this.event$.complete()
      // this.event$ = new Subject<TableEvent>()
    }
  }

  setIsSleeping(value: boolean) {
    this.isSleeping = value
  }

  addBots(quantity: number, startingChips: number) {
    const players = this.gamePlayers()
    const botPlayers = players.filter((player) => player.kind === PlayerKind.BOT)
    const botPlayersLastNumber = botPlayers.length
      ? Math.max(
          ...botPlayers.map((bot) => {
            const match = bot.name.match(/\d+$/)
            return match ? parseInt(match[0], 10) : 0
          })
        )
      : 0

    const playerBots: Player[] = Array.from({ length: quantity }).map((_, index) => {
      const botUserId = nanoid().split('-').join('').slice(0, 24)
      const botKinds = Object.values(BotKind)
      const botKind: BotKind = botKinds[Math.floor(Math.random() * botKinds.length)]
      const name = `\u{1F916} Bot ${botPlayersLastNumber + index + 1}`
      const player = new Player(
        name,
        startingChips,
        botUserId,
        ['user'],
        PlayerKind.BOT,
        undefined,
        undefined,
        undefined,
        undefined,
        botKind
      )

      if (this.board.canSeatNewPlayers() || this.isReset()) {
        this.putNewPlayerToPlay(player)
      } else {
        this.waitlistPlayer(player)
      }

      return player
    })

    this.attemptToStartGameTimer()
    this.attemptToStartNewHand()
    const eventVersion = this.updateState({ name: TableEventKind.BotsManuallyAdded })
    this.emitEvent({ ...TableEvents.botsManuallyAddedEvent(this), ...eventVersion })
    this.completeEvents()
    return playerBots
  }

  addPlayer({
    name,
    chips,
    userId,
    userRoles,
    canNotPlay,
    playerKind,
    botKind,
    isThreeCardGame,
    shouldCompleteEvents = true
  }: AddPlayerOptions) {
    logger.trace(`[Table.addPlayer] ${name}, ${chips}, ${userId}`)
    let player: Player | undefined
    if (userId) {
      player = this.getPlayerByUserId(userId, true)
    }

    if (!player) {
      if (userId === name) {
        name = Language.strings.table.autoPlayerName(this.totalPlayers() + 1)
      }

      player = new Player(
        name,
        chips,
        userId,
        userRoles,
        playerKind,
        undefined,
        undefined,
        undefined,
        undefined,
        botKind
      )
      if (canNotPlay || this.isTableFull) {
        this.setPlayerAsObserver(player, userRoles)
      } else {
        this.addPlayerToPlay(player, userRoles)
      }

      if (!isThreeCardGame) {
        this.attemptToStartGameTimer()
      }
    } else {
      const eventVersion = this.updateState({ name: TableEventKind.PlayerJoined, forPlayerId: player.id })
      this.emitEvent({ ...TableEvents.playerJoinedEvent(this, player), ...eventVersion })
    }

    if (shouldCompleteEvents) {
      this.completeEvents()
    }

    return player
  }

  private attemptToStartGameTimer() {
    if (this.isReadyToPlay() && !GameModes.isThreeCard(this.mode) && this.gamePlayers().length >= this.minPlayers) {
      //emit start timer modal event
      const eventVersion = this.updateState({ name: TableEventKind.TimerStarted })
      this.emitEvent({ ...TableEvents.tableTimerStartedEvent(this), ...eventVersion })
    }
  }

  private attemptToStartNewHand() {
    if (this.playingPlayers().length >= this.minPlayers && this.isReset()) {
      if (this.isPaused()) {
        this.unpause()
      }
      if (!this.isPlaying()) {
        this.newHand()
      }
    }
  }

  private putNewPlayerToPlay(player: Player) {
    player.beginPlay()

    if (!this.getPlayerById(player.getId(), true)) {
      this.players.splice(this.getButtonPlayerIndex(), 0, player)

      // this.board.addPlayerBettingSeatAt(
      //   this.getPlayerIndexByPlayerId(player.getId()),
      // )
    }

    if (!this.board.pot.getPlayerIds().includes(player.getId())) {
      /** Make sure this player gets added to the main pot */
      this.board.pot.players.push(player)
    }

    this.updatePlayersFromWaitlist()

    this.adjustTheButton()

    // NOTE: we need to reseat the bets to correct players
    this.resetPlayerSeats()

    this.setColorForPlayer(player.getId())

    if (this.state.tag === GameStates.PreGame.tag && this.gamePlayers().length >= this.minPlayers) {
      this.state = GameReducer(GameActions.CreateGame, this.state)
    }
  }

  addPlayerToPlay(player: Player, userRoles?: string[]) {
    logger.info(`[Table.addPlayerToPlay] playingPlayers.length ${this.playingPlayers().length}`)

    if ((this.board.canSeatNewPlayers() || this.isReset()) && this.playingPlayers().length <= this.maxPlayers) {
      if (
        this.gamePlayers().length >= this.maxPlayers ||
        (GameModes.isTournament(this.mode) && this.fecthTournamentRunningTime() > 15)
      ) {
        this.setPlayerAsObserver(player, userRoles)
      } else {
        this.putNewPlayerToPlay(player)
        this.attemptToStartNewHand()
        const eventVersion = this.updateState({ name: TableEventKind.PlayerJoined, forPlayerId: player.id })
        this.emitEvent({ ...TableEvents.playerJoinedEvent(this, player), ...eventVersion })
      }
    } else {
      logger.info(`[Table.addPlayerToPlay] all players, ${this.players.length}`)
      logger.info(`[Table.addPlayerToPlay] game players, ${this.gamePlayers().length}`)

      if (
        this.gamePlayers().length >= this.maxPlayers ||
        (GameModes.isTournament(this.mode) && this.fecthTournamentRunningTime() > 15)
      ) {
        this.setPlayerAsObserver(player, userRoles)
      } else {
        this.waitlistPlayer(player)

        // this.adjustButtonForWaitList(player)

        // this.startGameWithWaitListPlayers()
      }
    }
  }

  setPlayerAsObserver(player: Player, userRoles?: string[]) {
    player.observer()

    if (this.players[this.currentPlayer]) {
      this.previousPlayerId = this.players[this.currentPlayer].getId()
    }
    // Fetch observer list and assign observer id
    if (userRoles && userRoles.indexOf('teacher') !== -1) {
      const observers = this.observerPlayers().filter((p) => p?.userRoles?.indexOf('teacher') !== -1)
      const get10thobserver = observers.filter((o) => o.isTeachingObserver())
      if (get10thobserver.length === 0) {
        player.observerId = 1
      }
    }

    this.players.push(player)

    if (
      this.activePlayers().length > 0 &&
      this.players[this.currentPlayer] &&
      this.previousPlayerId !== this.players[this.currentPlayer].getId()
    ) {
      this.nextTurn()
    }

    const eventVersion = this.updateState({ name: TableEventKind.PlayerObserver, forPlayerId: player.id })
    this.emitEvent({ ...TableEvents.playerObserverEvent(this, player), ...eventVersion })
  }

  checkTableIsFull() {
    return this.gamePlayers().length === this.maxPlayers
  }

  fecthTournamentRunningTime() {
    logger.info(`${this.gameStartTime} gameStartTime ${this.getCurrentCheckpoint()} getCurrentCheckpoint`)
    const diff = Math.abs(this.gameStartTime - this.getCurrentCheckpoint())
    const minute = parseInt(((diff / (1000 * 60)) % 60).toFixed())
    logger.info(`${minute} minute`)
    return minute
  }

  startGameWithWaitListPlayers() {
    if (
      (this.players.length >= this.minPlayers && this.getOutList().length === this.players.length) ||
      this.getWaitlist().length === this.players.length
    ) {
      this.newHand()
    }
  }

  waitlistPlayer(player: Player) {
    player.wait()

    /**
     * This is a new player joining that has been waitlisted.
     * Splice them in behind the button player unless they
     * are the last player in the players list
     */
    if (!this.getPlayerById(player.getId(), true)) {
      this.previousPlayerId = this.currentPlayer !== -1 ? this.players[this.currentPlayer].getId() : ''
      this.players.splice(this.getButtonPlayerIndex(), 0, player)
      this.adjustTheButton()

      this.adjustTheButton()

      this.resetPlayerSeats()
      // this.board.addPlayerBettingSeatAt(this.getButtonPlayerIndex())
      if (this.currentPlayer !== -1 && this.previousPlayerId !== this.players[this.currentPlayer].getId()) {
        // TODO: is this necessary? this is throwing unnecessary events such as end_of_turn capture_last_action hand_current_player
        //this.nextTurn()
      }
    }
    // Set player color
    this.setColorForPlayer(player.getId())

    const eventVersion = this.updateState({ name: TableEventKind.PlayerWaitlisted, forPlayerId: player.id })
    this.emitEvent({ ...TableEvents.playerWaitlistedEvent(this, player), ...eventVersion })
  }

  resetPlayerSeats() {
    // TODO: This needs a refactor
    const bets: Bet[] = []
    this.players.map((player) => {
      const seatId = player.getSeatId()
      const currentSeatId = this.getPlayerIndexByPlayerId(player.getId())
      const bet = this.board.getBetForPlayerAt(seatId)
      if (player.isOut() || player.isWaiting()) {
        player.setSeatId(currentSeatId)
        bets[currentSeatId] = new Bet(0, BetKind.BLANK)
      } else if (bet) {
        player.setSeatId(currentSeatId)
        bets[currentSeatId] = bet
      }
    })
    if (this.leftPlayers.length > 0) {
      this.leftPlayers.map((player) => {
        if (player.bet > 0 || player.bets.length > 0) {
          bets.push(player.bets[0])
        }
      })
    }
    if (this.leftPlayers.length > 1 && this.board.getBets().length > bets.length) {
      this.compareBets(bets)
    } else {
      this.board.setBets(bets)
    }
    // this.leftPlayers = []
  }

  compareBets(bets) {
    const previousBets = this.board.getBets()
    const currentBets = bets

    const extraObjects = []
    previousBets.map((elementOne) => {
      // Check if the element exists in array two
      const existsInArrayTwo = currentBets.some(
        (elementTwo) =>
          elementOne?._amount > 0 &&
          elementTwo?._amount === elementOne?._amount &&
          elementTwo?._kind === elementOne?._kind
      )
      if (!existsInArrayTwo) {
        extraObjects.push(elementOne)
      }
    })
    // Push extra objects to array two
    currentBets.push(...extraObjects)
    this.board.setBets(currentBets)
  }

  adjustButtonForWaitList(player: Player) {
    if (this.buttonPlayerId !== '') {
      const buttonPlayerIndex = this.getPlayerIndexByPlayerId(this.buttonPlayerId)
      if (buttonPlayerIndex !== this.getButtonPlayerIndex()) {
        this.button = buttonPlayerIndex

        if (this.players.indexOf(player) === this.currentPlayer) {
          this.currentPlayer = this.isHeadsUpPlay() ? this.getButtonPlayerIndex() : this.countFromButton(3)

          if (
            this.players[this.currentPlayer].isFolded() ||
            this.players[this.currentPlayer].isWaiting() ||
            this.players[this.currentPlayer].isOut()
          ) {
            this.nextPlayer()
          }
        }
      }
    }
  }

  newHand() {
    if (!this.isPaused()) {
      // NewHand event syncs player action timers
      this.playerActionTimeLeft = this.playerActionTime
      this.players.map((p) => {
        const isPlayerOut = p.isForeverOut() || p.isSittingOut() || p.isOut()
        if (!isPlayerOut) {
          p.setCheckpointManually(this.currentCheckpoint())
        }
      })
    }
    this.updateCardsVisibilityOnShowDown(false)
    const eventVersion = this.updateState({ name: TableEventKind.HandNew })
    this.emitEvent({ ...TableEvents.handNewEvent(this), ...eventVersion })
    this.completeEvents()
  }

  getPlayerActionTimeLeft() {
    if (this.isPaused()) {
      return this.playerActionTimeLeft
    }
    const activeSortedPlayers = orderBy(this.gamePlayers(), 'checkpoint').filter(
      (p) => p.state.tag !== PlayerStates.Waiting.tag
    )

    const currentCheckpoint = this.getCurrentCheckpoint()

    const lastActionCheckpoint =
      activeSortedPlayers && activeSortedPlayers.length > 0 ? activeSortedPlayers.at(-1).checkpoint : currentCheckpoint

    const timeElapsed = currentCheckpoint - lastActionCheckpoint

    this.playerActionTimeLeft = this.playerActionTime - (timeElapsed >= 1000 ? timeElapsed : 0)
    return Math.sign(this.playerActionTimeLeft) === -1 ? 0 : this.playerActionTimeLeft
    // const isStreetEvent = this.board.isFlop() || this.board.isTurn() || this.board.isRiver()
    // return isStreetEvent ? this.playerActionTime : this.playerActionTimeLeft
  }

  pause(shouldEmitEvent = true, isSleeping = false) {
    logger.info('[Table.pause]')
    this.gamePauseTime = this.getCurrentCheckpoint()
    this.isSleeping = isSleeping

    if (shouldEmitEvent) {
      const eventVersion = this.updateState({ name: TableEventKind.Paused })
      this.emitEvent({ ...TableEvents.tablePausedEvent(this), ...eventVersion })
    }

    this.completeEvents()
  }

  sleep() {
    logger.info('[Table.sleep]')
    this.gamePauseTime = this.getCurrentCheckpoint()
    const eventVersion = this.updateState({ name: TableEventKind.Sleep })
    this.emitEvent({ ...TableEvents.tableSleepEvent(this), ...eventVersion })
    // this.state = GameReducer(GameActions.SleepPlay, this.state)
  }

  unpause(shouldEmitEvent = true) {
    logger.info('[Table.unpause]')
    const activeSortedPlayers: Player[] = orderBy(this.gamePlayers(), 'checkpoint').filter(
      (p) => p.state.tag !== PlayerStates.Waiting.tag
    )

    this.isSleeping = false

    const currentCheckpoint = this.getCurrentCheckpoint()
    const lastActionCheckpoint =
      activeSortedPlayers && activeSortedPlayers.length > 0
        ? (activeSortedPlayers[activeSortedPlayers.length - 1].checkpoint ?? currentCheckpoint)
        : currentCheckpoint

    const currentPlayer = this.getCurrentPlayer()
    if (currentPlayer) {
      const timeElapsed = currentCheckpoint - this.gamePauseTime // time elapsed from when the game was paused to when it is unpaused
      currentPlayer.setCheckpointManually(lastActionCheckpoint + timeElapsed) // start when we paused the game
    }

    // logger.info(`[Table.unpause] this.getCurrentCheckpoint() ${this.getCurrentCheckpoint()}`)
    // logger.info(`[Table.unpause] new getCurrentPlayer()?.checkpoint ${this.getCurrentPlayer()?.checkpoint}`)
    // logger.info(`[Table.unpause] this.playerActionTimeLeft ${this.playerActionTimeLeft}`)
    // logger.info(`[Table.unpause] getPlayerActionTimeLeft() ${this.getPlayerActionTimeLeft()}`)
    // logger.info(`[Table.unpause] TABLE_CONFIG this.playerActionTime ${this.playerActionTime}`)

    if (shouldEmitEvent) {
      // Specific event to trigger toast
      const eventVersion = this.updateState({ name: TableEventKind.Resumed })
      this.emitEvent({ ...TableEvents.tableResumedEvent(this), ...eventVersion })

      const eventVersion2 = this.updateState({ name: TableEventKind.Started })
      this.emitEvent({ ...TableEvents.tableStartedEvent(this), ...eventVersion2 })
    }

    // this.completeEvents()
  }

  collectCards() {
    this.players.map((p) => p.returnCards())
  }

  dealCards() {
    this.activePlayers().map((p) => {
      p.incrementNumberOfHandsPlayed()
      p.getCards(this.board.dealHoleCards())
    })

    const eventVersion = this.updateState({ name: TableEventKind.HandDealtCards })
    this.emitEvent({ ...TableEvents.handDealtCardsEvent(this), ...eventVersion })
  }

  getPlayersWithChips() {
    return this.players.filter((p) => !p.isObserver() && p.chips > 0)
  }

  checkIfBigBlindWentOut() {
    // logger.trace(`[Table.checkIfBigBlindWentOut] this.isDeadSmallBlindCurrentHand ${this.isDeadSmallBlindCurrentHand}`)
    if (this.isDeadSmallBlindCurrentHand) {
      this.isDeadSmallBlindNextHand = true
    }
    const playersWithChips = this.getPlayersWithChips()
    const bigBlindOut = this.players.filter(
      (p) => p.role === 'BB' && (p.chips <= 0 || p.isForeverOut() || p.isWaiting())
    )
    const isSmallBlindOut =
      this.players.filter((p) => p.role === 'SB' && (p.chips <= 0 || p.isForeverOut() || p.isWaiting())).length > 0
    logger.trace(`[Table.checkIfBigBlindWentOut] this.playingPlayers().length ${this.playingPlayers().length}`)
    const isBigBlindOut =
      this.isHeadsUpPlay() || playersWithChips.length === 2
        ? false
        : bigBlindOut.length > 0 && this.playingPlayers().length > 2
    this.isDeadSmallBlindCurrentHand = isBigBlindOut
    this.isDeadDealerCurrentHand = isBigBlindOut && isSmallBlindOut
    // logger.trace(`[Table.checkIfBigBlindWentOut] this.isDeadSmallBlindCurrentHand ${this.isDeadSmallBlindCurrentHand}`)
  }

  /**
   * Sets the dealer/button position on the board
   * * The first time the button is set it must be `players.length - 1`
   * * Subsequent times the button is set, it must be progressed by 1
   * * If the small blind leaves during a previous hand, the progression
   *   is interrupted to accommodate a `dead button`
   */
  setTheButton() {
    // logger.trace(`STB: button before moving: ${this.button}`)
    // logger.trace(`STB: current player before moving: ${this.button}`)

    if (this.buttonPlayerId === '') {
      const activePlayers = this.activePlayers()
      this.button = this.getPlayerIndexByPlayerId(activePlayers[activePlayers.length - 1].getId())
    } else {
      this.button =
        this.isDeadSmallBlindNextHand && this.playingPlayers().length > this.minPlayers
          ? this.button
          : this.isDeadDealerCurrentHand
            ? this.countFromButton(0)
            : this.countFromButton(1)
    }
    // If button player same then fetch button again
    if (this.players[this.button].getId() === this.buttonPlayerId) {
      this.button =
        this.isDeadSmallBlindNextHand && this.playingPlayers().length > this.minPlayers
          ? this.button
          : this.isDeadDealerCurrentHand
            ? this.countFromButton(0)
            : this.countFromButton(1)
    }

    this.buttonPlayerId = this.players[this.button].getId()

    this.currentPlayer = this.isHeadsUpPlay()
      ? this.getButtonPlayerIndex()
      : this.isDeadSmallBlindCurrentHand
        ? this.countFromButton(2)
        : this.countFromButton(3)

    if (this.players[this.currentPlayer].doNoActionCount === this.maxDoNoActionCount) {
      while (
        !this.players[this.currentPlayer].isInPlay() ||
        this.players[this.currentPlayer].doNoActionCount === this.maxDoNoActionCount
      ) {
        this.currentPlayer = (this.currentPlayer + 1) % this.players.length
      }
    }

    this.currentPlayerId = this.players[this.currentPlayer].getId()

    this.smallBlindLeft = false
    this.bigBlindLeft = false

    // logger.trace(`STB: set the button: ${this.button}`)
    // logger.trace(`STB: set current player: ${this.currentPlayer}`)

    // Set player role to identify SB and BB
    this.setRoleForPlayers()
  }

  setRoleForPlayers() {
    // logger.trace(`[Table.setRoleForPlayers] isDeadSmallBlindCurrentHand ${this.isDeadSmallBlindCurrentHand}`)
    this.players.map((player, index) => {
      if (this.isDeadSmallBlindCurrentHand) {
        if (index === this.getSmallBlindPlayerIndex()) {
          player.role = 'BB'
        } else if (this.getButtonPlayer().id === player.id) {
          player.role = 'D'
        } else {
          player.role = ''
        }
      } else {
        if (index === this.getSmallBlindPlayerIndex()) {
          player.role = 'SB'
        } else if (index === this.getBigBlindPlayerIndex()) {
          player.role = 'BB'
        } else if (this.getButtonPlayer().id === player.id) {
          player.role = 'D'
        } else {
          player.role = ''
        }
      }
    })
  }

  adjustTheButton() {
    if (this.buttonPlayerId !== '') {
      const buttonPlayerIndex = this.getPlayerIndexByPlayerId(this.buttonPlayerId)

      if (buttonPlayerIndex !== this.getButtonPlayerIndex()) {
        this.button = buttonPlayerIndex
      }
    }

    if (this.currentPlayerId !== '') {
      this.currentPlayer = this.getPlayerIndexByPlayerId(this.currentPlayerId)
    }
  }

  nextPlayer() {
    this.updatePlayingPlayers()
    // logger.trace(`[Table.nextPlayer] NCP: current player before: ${this.currentPlayer}`)
    this.currentPlayer = (this.currentPlayer + 1) % this.players.length
    const smallBlindPlayer = this.getSmallBlindPlayer()
    const bigBlindPlayer = this.getBigBlindPlayer()

    // logger.trace(`[Table.nextPlayer] inPlayPlayers: ${this.inPlayPlayers().length}`)
    // logger.trace(`[Table.nextPlayer] board.state: ${JSON.stringify(this.board.internalState())}`)

    if (this.inPlayPlayers().length >= 2 && !this.board.isDeal() && !this.board.hasBets()) {
      // logger.trace(`[Table.nextPlayer] totalPlayers(): ${this.totalPlayers()}`)
      if (
        !(this.smallBlindFold || this.smallBlindLeft) &&
        smallBlindPlayer &&
        smallBlindPlayer.isInPlay()
        // smallBlindPlayer?.getId() !== this.currentPlayerId
      ) {
        // logger.info(`[Table.nextPlayer] smallBlindPlayer: ${smallBlindPlayer.name} ${smallBlindPlayer.id}`)
        // According to heads up poker rules, the dealer posts the small blind and the other player posts
        // the big blind. The first card is dealt to the player in the Big Blind. The dealer acts first
        // pre-flop and the big blind acts first on the flop, turn, and river. The dealer always goes last
        // on the flop, turn, and river.
        if (this.isHeadsUpPlay() && (this.board.isFlop() || this.board.isTurn() || this.board.isRiver())) {
          if (this.getBigBlindPlayer()) {
            this.currentPlayer = this.players.indexOf(this.getBigBlindPlayer())
          }
        } else {
          if (this.getSmallBlindPlayer()) {
            this.currentPlayer = this.players.indexOf(this.getSmallBlindPlayer())
          }
        }
      } else if (
        !(this.bigBlindFold || this.bigBlindLeft) &&
        bigBlindPlayer &&
        bigBlindPlayer.isInPlay()
        // bigBlindPlayer?.getId() !== this.currentPlayerId
      ) {
        logger.info(`[Table.nextPlayer] bigBlindPlayer: ${bigBlindPlayer}`)
        this.currentPlayer = this.players.indexOf(this.getBigBlindPlayer())
      } else {
        this.currentPlayer = this.players.indexOf(this.getBigBlindPlayer())
        this.currentPlayer = (this.currentPlayer + 1) % this.players.length
      }
      // logger.trace(`[Table.nextPlayer] NCP: set player after flop, turn, or river: ${this.currentPlayer}`)
    }

    while (!this.players[this.currentPlayer].isInPlay()) {
      this.currentPlayer = (this.currentPlayer + 1) % this.players.length
    }

    this.currentPlayerId = this.players[this.currentPlayer].getId()

    // logger.trace(`[Table.nextPlayer] NCP: set the current player: ${this.currentPlayer}`)

    // if (!this.isPaused()) {
    //   // NewHand event syncs player action timers
    //   this.playerActionTimeLeft = this.playerActionTime
    //   this.players.map((p) => p.setCheckpointManually(this.currentCheckpoint()))
    // }

    const eventVersion = this.updateState({
      name: TableEventKind.HandCurrentPlayer,
      forPlayerId: this.getCurrentPlayer().id
    })
    this.emitEvent({ ...TableEvents.handCurrentPlayerEvent(this, this.getCurrentPlayer()), ...eventVersion })
  }

  progressHandTo(state: Extract<BoardStateTag, 'river' | 'showdown'>, dealNextCards = true, emitEvents = true) {
    // logger.trace('[Table.nextTurn] only one player with actions, progressing hand to showdown')
    this.updateCardsVisibilityOnShowDown(state === 'showdown')
    const everyoneFolded = this.checkEveryoneFolded()
    while (state === 'showdown' ? !this.board.isRiver() : !this.board.isTurn()) {
      this.nextBoardAction(!everyoneFolded && dealNextCards)
      if (!everyoneFolded && emitEvents) {
        // logger.trace('[Table.nextTurn] allPlayersAreAllIn streetFinishedEvent')
        const eventVersion = this.updateState({ name: TableEventKind.StreetFinished })
        this.emitEvent({ ...TableEvents.streetFinishedEvent(this), ...eventVersion })
      }
    }
  }

  nextTurn(shouldComplete = true) {
    if (this.isFinished()) {
      // no-op
      this.completeEvents()
      return
    }
    logger.trace('[Table.nextTurn]')
    const eventVersion = this.updateState({ name: TableEventKind.PlayerEndOfTurn })
    this.emitEvent({ ...TableEvents.playerEndOfTurn(this), ...eventVersion })

    // Reset user timed out
    this.players.map((p) => p.setUserTimedOut(false))

    // Downgrade biggestBetter's All-In to Call to match second largest All-In
    // if (this.allPlayersAreAllIn() || this.playersHaveFinishedBetting()) {
    //   this.downGradeBets()
    // }
    if (this.allPlayersAreAllIn() || (this.playersHaveFinishedBetting() && this.activePlayers().length === 2)) {
      this.downGradeBets()
    }

    this.captureLastPlayerAction()

    if (this.allPlayersAreAllIn()) {
      this.updateCardsVisibilityOnShowDown(true)
      this.checkHighestBetPlayer()
    }

    if (this.checkEveryoneFolded()) {
      this.addBoardDealState()
    }

    if (this.shouldForceGameOver()) {
      this.checkGameOver()
      this.completeEvents()
      return
    }

    logger.trace(`[Table.nextTurn] board isFlop ${JSON.stringify(this.board.state)}`)

    if (this.checkEveryoneFolded() || this.playersHaveFinishedBetting() || this.allPlayersAreAllIn()) {
      /** check for street finishing events: betting complete, all folded, all are all in */
      logger.trace('[Table.nextTurn] street is finished, capturing bets')

      this.captureBets()

      if (GameModes.isThreeCard(this.mode) && this.board.internalState() !== 'river') {
        this.progressHandTo('river', false, false) //goes to turn
      }

      if (this.allPlayersAreAllIn() || this.oneActivePlayerLeft()) {
        this.progressHandTo('showdown') //goes to river
      } else {
        const isStreetFinished = !(this.checkEveryoneFolded() || this.allPlayersAreAllIn())
        if (isStreetFinished) {
          logger.trace(`[Table.nextTurn] isStreetFinished ${isStreetFinished}`)
          const eventVersion2 = this.updateState({ name: TableEventKind.StreetFinished })
          this.emitEvent({ ...TableEvents.streetFinishedEvent(this), ...eventVersion2 })
        }
      }

      this.nextBoardAction()

      if (this.board.isShowdown()) {
        // Card visible before show down
        if (this.allActivePlayersWithChips().length < 2) {
          this.updateCardsVisibilityOnShowDown(true)
        }
        /** if this is the showdown, complete the hand */
        this.doShowdown()
        // if we are in tournament mode, check if the game is over
        const playersWithChips = this.getPlayersWithChips()
        if (this.isTournament() || this.isOpenTable()) {
          logger.trace('[Table.nextTurn] check if game is over...')
          logger.trace(`[Table.nextTurn] outPlayers().length ${this.outPlayers().length}`)
          logger.trace(`[Table.nextTurn] playersWithChips.length ${playersWithChips.length}`)
          if (playersWithChips.length === 1) {
            logger.trace('[Table.nextTurn] table finished and game over')
            if (this.autostart) {
              this.newHand()
            }
            this.finishTable()
            this.completeEvents()
            return
          }
        }

        const threeCardIsFinished =
          GameModes.isThreeCard(this.mode) && this.amountOfHands === THREE_CARD_AMOUNT_OF_HANDS
        if (threeCardIsFinished) {
          this.finishTable()
          this.completeEvents()
          return
        }

        if (!this.isFinished()) {
          // if the game is finished, close out and cleanup
          logger.trace('[Table.nextTurn] game is finished')
          if (this.autostart) {
            this.newHand()
          } else {
            // We should do this.softReset(), because the resume game service will resume the game on the step function
            // but tests are not meant to be running on step functions
            // trigger hand new after every one folded
            if (!isTestingWithJest) {
              this.softReset()
            } else {
              // This is a workaround for tests to pass since step function do not work locally
              if (this.checkEveryoneFolded() && !GameModes.isThreeCard(this.mode)) {
                this.newHand()
              } else {
                this.softReset()
              }
            }
            // if no players are out, auto-start (no need to wait for rebuys)
            // const outPlayers = this.outPlayers()
            // if (outPlayers.length === 0) {
            //   this.newHand()
            // }
          }
        }

        this.completeEvents()
        return
      }

      // move to the button go to next turn, starts at big blind
      if (!this.getBigBlindPlayer()?.isFolded()) {
        this.currentPlayer = this.countFromButton(2)
      }
      this.gamePlayers().map((p) => p.resetBets())
      this.setPlayersLastAction()
    }

    const player = this.getCurrentPlayer()
    if (this.playerIsDisconnected(player)) {
      /** disconnected players are skipped, update to next player and continue */
      logger.trace('[Table.nextTurn] player disconnected, skipping')
    }

    if (!this.checkEveryoneFolded()) {
      this.nextPlayer()
    }

    if (shouldComplete) this.completeEvents()
  }

  outAllGamePlayers() {
    const gamePlayers = this.gamePlayers()
    gamePlayers.map((op) => {
      op.out()
    })
  }

  forceGameOver() {
    this.finishTable()
  }

  shouldForceGameOver() {
    const gamePlayers = this.players
    const botPlayers = gamePlayers.filter((player) => player.kind === PlayerKind.BOT)
    const humanPlayers = gamePlayers.filter((player) => player.kind === PlayerKind.HUMAN && !player.isObserver())
    const currentCheckpoint = this.getCurrentCheckpoint()
    const humanSittingOutPlayers = humanPlayers.filter(
      (player) => player.isSittingOut() || player.isOut() || player.isForeverOut()
    )
    const tournamentJoinTime = this.tournamentJoinTime
    const tournamentRegistrationWindow = this.gameStartTime + tournamentJoinTime * 60 * 1000

    const allHumansSittingOut =
      humanSittingOutPlayers.length > 0 && humanPlayers.length === humanSittingOutPlayers.length

    const onlyBotsLeft = humanPlayers.length === 0 && botPlayers.length > 0
    const onlyBotsArePlaying = allHumansSittingOut && botPlayers.length > 0 // check if bots are actually playing
    const registrationIsOver = currentCheckpoint > tournamentRegistrationWindow

    const activeSortedHumanPlayers = orderBy(humanPlayers, 'checkpoint').filter(
      (p) => p.state.tag !== PlayerStates.Waiting.tag
    )

    const lastActionCheckpoint =
      activeSortedHumanPlayers && activeSortedHumanPlayers.length > 0
        ? activeSortedHumanPlayers.at(-1).checkpoint
        : currentCheckpoint

    const timeElapsed = currentCheckpoint - lastActionCheckpoint
    const isGameInactive = timeElapsed > TABLE_CONFIG.INACTIVITY_DURATION_TO_KICK
    const shouldEndGameAfterInactivity = isGameInactive && allHumansSittingOut

    logger.debug(
      `[checkGameOver] checking game over botsAreLeft ${onlyBotsLeft} , numberOfPlayers ${humanPlayers.length}`
    )

    logger.debug(
      `[checkGameOver] gamePlayers ${JSON.stringify(gamePlayers.map((p) => ({ name: p.name, state: p.state })))}`
    )

    const isPractice = this.isPractice() || this.isPracticeTournament()

    const onePlayerLeftInTournament =
      (this.gamePlayers().length === 1 || this.isTournamentFinished()) && this.isTournament()

    if (
      (onlyBotsLeft && (registrationIsOver || this.isOpenTable() || isPractice)) ||
      (onlyBotsArePlaying && shouldEndGameAfterInactivity) ||
      onePlayerLeftInTournament
    ) {
      return true
    }

    return false
  }

  checkGameOver() {
    if (this.isFinished()) return

    if (this.shouldForceGameOver()) {
      logger.debug('[checkGameOver] forcing game over')
      this.forceGameOver()
      this.isGameOverForced = true
      logger.debug(`[checkGameOver] forcing game over ${this.isFinished()} ${JSON.stringify(this.state)}`)
    }
  }

  isTournamentFinished() {
    return (
      this.getWaitlist().length === 1 &&
      this.getSitOutPlayers().length === 0 &&
      this.gamePlayers().length - 1 === this.outPlayers().length
    )
  }

  checkHighestBetPlayer() {
    const highestBetPlayer = this.allInPlayers().filter((p) => p.bet === this.board.biggestBet())
    const secondHighestBetPlayer = this.allInPlayers().find(
      (p) => p.bet < this.board.biggestBet() && p.bet === this.board.secondBiggestBet()
    )
    if (highestBetPlayer.length === 1 && secondHighestBetPlayer) {
      const player = highestBetPlayer[0]
      player.chips = player.bet - secondHighestBetPlayer.bet
      player.bet = secondHighestBetPlayer.bet
      const updateBet = new Bet(player.bet, BetKind.BET)
      const index = player.bets.length - 1
      player.bets.splice(index, 1)
      player.bets.push(updateBet)
      const currentSeatId = this.getPlayerIndexByPlayerId(player.getId())
      this.board.doBetForPlayerAt(currentSeatId, updateBet)
    }

    const eventVersion = this.updateState({
      name: TableEventKind.HighestBetPlayer,
      forPlayerId: this.getCurrentPlayer().id
    })
    this.emitEvent({ ...TableEvents.higestBetPlayerEvent(this, this.getCurrentPlayer()), ...eventVersion })
  }

  updateCardsVisibilityOnShowDown(isCardsVisible: boolean) {
    if (!this.checkEveryoneFolded()) {
      this.isCardsVisibleOnShowDown = isCardsVisible
      const eventVersion = this.updateState({ name: TableEventKind.CardsVisibleOnShowDownEvent })
      this.emitEvent({ ...TableEvents.cardsVisibleOnShowDownEvent(this), ...eventVersion })
    }
  }

  allActivePlayersWithChips() {
    return this.activePlayers().filter((p) => p.chips > 0 && p.isInPlay())
  }

  captureLastPlayerAction() {
    // Set lastAction to show on ui
    this.setPlayersLastAction()
    const eventVersion = this.updateState({ name: TableEventKind.CaptureLastAction })
    this.emitEvent({ ...TableEvents.captureLastAction(this), ...eventVersion })
  }

  setPlayersLastAction() {
    this.players.map((p) => {
      if (p.isForeverOut()) {
        p.lastAction.action = PlayerStates.ForeverOut.tag
        p.lastAction.amount = 0
      }
      if (p.isAllIn()) {
        p.lastAction.action = PlayerStates.AllIn.tag
        p.lastAction.amount = p.bet === 0 ? p.lastAction.amount : p.bet
      }
      if (p.isFolded()) {
        p.lastAction.action = PlayerStates.Folded.tag
        p.lastAction.amount = 0
      }
      if (p.isWaiting()) {
        p.lastAction.action = PlayerStates.Waiting.tag
        p.lastAction.amount = 0
      }
      if (p.isOut()) {
        p.lastAction.action = PlayerStates.Out.tag
        p.lastAction.amount = 0
      }
      if (p.isInPlay()) {
        const betKind: Bet = p.bets[p.bets.length - 1]
        if (betKind?.kind === BetKind.SMALLBLIND || betKind?.kind === BetKind.BIGBLIND) {
          p.lastAction.action = betKind.kind
          p.lastAction.amount = betKind.amount
        } else {
          p.lastAction.action = betKind ? betKind.kind : ''
          p.lastAction.amount = betKind !== BetKind.CHECK ? p.bet : 0
        }
      }
      if (p.downGradeState === PlayerStates.InPlay.tag) {
        p.lastAction.action = BetKind.CALL
        p.lastAction.amount = p.downGradeAmount
      }
    })
  }

  nextBoardAction(dealNextCards = true) {
    logger.trace('nextBoardAction')
    this.board.nextAction(dealNextCards)
    this.updatePlayingPlayers()
  }

  addBoardDealState() {
    if (this.board.isFlop()) {
      this.boardDealState = 'flop'
    }
    if (this.board.isTurn()) {
      this.boardDealState = 'turn'
    }
    if (this.board.isRiver()) {
      this.boardDealState = 'river'
    }
  }

  getCheckpoint() {
    return super.lastCheckpoint()
  }

  getCurrentCheckpoint() {
    return super.currentCheckpoint()
  }

  activePlayers() {
    return this.players.filter((p) => p.isInPlay() || p.isAllIn())
  }

  gamePlayers() {
    return this.players.filter(
      (p) =>
        p.isInPlay() ||
        p.isAllIn() ||
        p.isSittingOut() ||
        p.isForeverOut() ||
        p.isWaiting() ||
        p.isFolded() ||
        p.isOut()
    )
  }

  sittingOutPlayers() {
    return this.players.filter((p) => p.isSittingOut())
  }

  playingPlayers() {
    return this.players.filter((p) => p.isInPlay() || p.isAllIn() || p.isFolded())
  }

  foreverOutPlayers() {
    return this.players.filter((p) => p.isForeverOut())
  }

  inPlayPlayers() {
    return this.players.filter((p) => p.isInPlay())
  }

  observerPlayers() {
    return this.players.filter((p) => p.isObserver())
  }

  playersWithBets() {
    return this.players.filter((p) => p.bet > 0)
  }

  allInPlayers() {
    return this.players.filter((p) => p.isAllIn())
  }

  allPlayersAreAllIn() {
    return this.allInPlayers().length >= this.activePlayers().length
  }

  foldedPlayers() {
    return this.players.filter((p) => p.isFolded())
  }

  oneActivePlayerLeft() {
    return this.activePlayers().length === 1 || this.inPlayPlayers().length === 1
  }

  outPlayers() {
    return this.players.filter((p) => !(p.isForeverOut() || p.isDisconnected() || p.isObserver()) && p.chips <= 0)
  }

  countFromButton(times: number) {
    if (this.allPlayersAreAllIn()) {
      return this.button
    }
    return this.countToNextInPlayPlayer(this.getButtonPlayer().getId(), times)
  }

  playerIsSitOut(player) {
    return this.isTournament() && player.doNoActionCount === this.maxDoNoActionCount && player.chips > 0
  }

  countToNextInPlayPlayer(fromPlayerId: string, times: number) {
    let position = this.getPlayerIndexByPlayerId(fromPlayerId)

    while (times > 0) {
      position = (position + 1) % this.players.length
      if (
        this.players[position] &&
        (this.players[position].isInPlay() ||
          this.players[position].isAllIn() ||
          this.players[position].isFolded() ||
          this.playerIsSitOut(this.players[position]))
      ) {
        times = times - 1
      }
    }
    // @TODO: we cannot return -1, if times is 0 and the button dealer is out
    // ************ FIX, return 0?
    return position
  }

  getPlayerByName(playerName: string) {
    const player = this.players.find((p) => p.name === playerName)
    if (player) return player
    throw new Error(`getPlayerByName: ${playerName} not found`)
  }

  getPlayerById(playerId: string): Player
  getPlayerById(playerId: string, nullable: boolean): Player | undefined
  getPlayerById(playerId: string, nullable?: boolean) {
    const player = this.players.find((p) => p.getId() === playerId)
    if (nullable || player) return player
    throw new Error(`getPlayerById: ${playerId} not found`)
  }

  getPlayerByUserId(userId: string): Player
  getPlayerByUserId(userId: string, nullable: boolean): Player | undefined
  getPlayerByUserId(userId: string, nullable?: boolean) {
    const player = this.players.find((p) => p.userId === userId)
    if (nullable || player) return player
    throw new Error(`getPlayerByUserId: ${userId} not found`)
  }

  getLeftPlayerByUserId(userId: string) {
    return this.leftPlayers.find((p) => p.userId === userId)
  }

  getPlayerIndexByPlayerId(playerId: string) {
    return this.players.filter((p) => p).findIndex((p) => p.getId() === playerId)
  }

  getButtonPlayer() {
    return this.players[this.getButtonPlayerIndex()]
  }

  getButtonPlayerIndex() {
    return this.button > 0 ? this.button : 0
  }

  getCurrentPlayer() {
    return this.players[this.getCurrentPlayerIndex()]
  }

  getCurrentPlayerIndex() {
    return this.currentPlayer
  }

  getSmallBlindPlayer() {
    if (this.smallBlindLeft) {
      return undefined
    }
    return this.players.find((p) => p.role === 'SB')
    // return this.players[this.getSmallBlindPlayerIndex()]
  }

  getSmallBlindPlayerIndex() {
    return this.isHeadsUpPlay() ? this.getButtonPlayerIndex() : this.countFromButton(1)
  }

  getBigBlindPlayer() {
    if (this.bigBlindLeft) {
      return undefined
    }
    return this.players.find((p) => p.role === 'BB')
    // return this.players[this.getBigBlindPlayerIndex()]
  }

  getBigBlindPlayerIndex() {
    return this.isHeadsUpPlay() ? this.countFromButton(1) : this.countFromButton(2)
  }

  getWaitlist() {
    return this.players.filter((p) => p.isWaiting())
  }

  getWaitlistPlayerById(playerId: string) {
    return this.getWaitlist().find((p) => p.getId() === playerId)
  }

  getWaitlistPlayerByUserId(userId: string) {
    return this.getWaitlist().find((p) => p.userId === userId)
  }

  getWaitlistPlayerIndex(playerId: string) {
    return this.getWaitlist().findIndex((p) => p.getId() === playerId)
  }

  getOutList() {
    return this.players.filter((p) => p.isOut())
  }

  getOutPlayerById(playerId: string) {
    return this.getOutList().find((p) => p.getId() === playerId)
  }

  getOutPlayerIndex(playerId: string) {
    return this.getOutList().findIndex((p) => p.getId() === playerId)
  }

  getOutPlayerByUserId(userId: string) {
    return this.getOutList().find((p) => p.userId === userId)
  }

  totalPlayers() {
    return this.players.length
  }

  isCurrentPlayer(playerId: string) {
    return this.getCurrentPlayer()?.getId() === playerId
  }

  isPlayerTurn(playerId: string) {
    if (!this.isCurrentPlayer(playerId)) {
      logger.trace(`[Table.isPlayerTurn] ${playerId} currentPlayer ${this.getCurrentPlayer()?.getId()}`)
      throw new PlayOutOfTurnError(this.getPlayerById(playerId).name)
    }
    return true
  }

  canPlayerTakeAction(playerId: string) {
    this.isPlayerTurn(playerId)
    if (this.isPaused()) {
      logger.trace(`[Table.canPlayerTakeAction] ${playerId} currentPlayer ${this.getCurrentPlayer()?.getId()}`)
      throw Error(`${playerId} cannot take action because game is paused`)
    }
    return true
  }

  doBetForPlayer(playerId: string, bet: Bet) {
    this.canPlayerTakeAction(playerId)
    const eventVersion = this.updateState({ name: TableEventKind.PlayerBet, forPlayerId: playerId, withBet: bet })
    this.emitEvent({ ...TableEvents.playerBetEvent(this, this.getPlayerById(playerId), bet), ...eventVersion })
    this.nextTurn()
  }

  doCallForPlayer(playerId: string) {
    logger.info('doCallForPlayer', playerId)
    this.canPlayerTakeAction(playerId)
    const eventVersion = this.updateState({ name: TableEventKind.PlayerCalled, forPlayerId: playerId })
    this.emitEvent({ ...TableEvents.playerCalledEvent(this, this.getPlayerById(playerId)), ...eventVersion })
  }

  doAllInForPlayer(playerId: string) {
    this.canPlayerTakeAction(playerId)
    const player = this.getPlayerById(playerId)

    this.doBetForPlayer(playerId, new Bet(player.chips + player.bet, BetKind.ALLIN))
  }

  doBuyInForSinglePlayer(playerId: string, chips: number) {
    const player = this.getPlayerById(playerId)
    if (player.isInPlay() && player.chips > 0) {
      player.addChips(chips)
      const eventVersion = this.updateState({ name: TableEventKind.PlayerAddChips, forPlayerId: playerId }, false)
      this.emitEvent({ ...TableEvents.playerAddChipsEvent(this, player), ...eventVersion })
    } else {
      player.buyChips(chips)
      const eventVersion = this.updateState({ name: TableEventKind.PlayerBuyIn, forPlayerId: playerId })
      this.emitEvent({ ...TableEvents.playerBuyInEvent(this, player), ...eventVersion })
    }
    this.completeEvents()
  }

  doBuyInForAllPlayers(chips: number) {
    const players = this.players.filter((player) => canReceiveChips(player.state))
    players.forEach((player) => {
      if (player.isInPlay() && player.chips > 0) {
        player.addChips(chips)
      } else {
        player.buyChips(chips)
      }
    })
    const eventVersion = this.updateState({ name: TableEventKind.BuyInForAllPlayers })
    this.emitEvent({ ...TableEvents.buyInForAllPlayers(this), ...eventVersion })
    this.completeEvents()
  }

  doCheckForPlayer(playerId: string) {
    this.canPlayerTakeAction(playerId)
    const eventVersion = this.updateState({ name: TableEventKind.PlayerChecked, forPlayerId: playerId })
    this.emitEvent({ ...TableEvents.playerCheckedEvent(this, this.getPlayerById(playerId)), ...eventVersion })
    this.nextTurn()
  }

  doFoldForPlayer(playerId: string, terminatingGame?: boolean, shouldComplete = true) {
    logger.trace(`doFoldForPlayer.playerId: ${playerId}`)

    // If is terminating game, player can fold even is game is paused.
    if (terminatingGame) {
      this.isPlayerTurn(playerId)
    } else {
      this.canPlayerTakeAction(playerId)
    }

    const player = this.getPlayerById(playerId)

    if (this.getBigBlindPlayer()?.getId() === player.getId()) {
      this.bigBlindFold = true
    }
    if (this.getSmallBlindPlayer()?.getId() === player.getId()) {
      this.smallBlindFold = true
    }

    player.fold()

    const eventVersion = this.updateState({ name: TableEventKind.PlayerFolded, forPlayerId: playerId })
    this.emitEvent({ ...TableEvents.playerFoldedEvent(this, player), ...eventVersion })

    this.nextTurn(shouldComplete)
  }

  doPostBlindForPlayer(playerId: string, bet: Bet) {
    const eventVersion = this.updateState({
      name: TableEventKind.PlayerPostedBlind,
      forPlayerId: playerId,
      withBet: bet
    })
    this.emitEvent({ ...TableEvents.playerPostedBlind(this, this.getPlayerById(playerId), bet), ...eventVersion })
  }

  payBlinds() {
    this.isSmallOrBigBlindForcedAllIn = false
    const bigBlindPlayer = this.getBigBlindPlayer()
    if (this.isDeadSmallBlindCurrentHand && this.board.isPreDeal() && bigBlindPlayer) {
      const bigBlindBetAmount = Math.min(this.blinds.big, bigBlindPlayer?.chips ?? 0)
      this.doPostBlindForPlayer(this.getBigBlindPlayer().getId(), new Bet(bigBlindBetAmount, BetKind.BIGBLIND))
    } else {
      if (this.board.isPreDeal() && this.getSmallBlindPlayer()) {
        const smallBlindPlayer = this.getSmallBlindPlayer()
        const smallBlindBetAmount = Math.min(this.blinds.small, smallBlindPlayer?.chips ?? 0)
        const bigBlindBetAmount = Math.min(this.blinds.big, bigBlindPlayer?.chips ?? 0)
        // If we only have 2 players and either the small or big are forced all in because they did have enough chips
        if (this.activePlayers().length === 2) {
          // if the small blind is all in, force the big blind to downgrade and match the only bet
          if (smallBlindPlayer?.chips <= this.blinds.small && smallBlindBetAmount < bigBlindBetAmount) {
            this.isSmallOrBigBlindForcedAllIn = true
            this.doPostBlindForPlayer(smallBlindPlayer.getId(), new Bet(smallBlindBetAmount, BetKind.SMALLBLIND))
            this.doPostBlindForPlayer(bigBlindPlayer.getId(), new Bet(smallBlindBetAmount, BetKind.BIGBLIND))
          } else if (bigBlindPlayer?.chips <= this.blinds.big && bigBlindBetAmount < smallBlindBetAmount) {
            this.isSmallOrBigBlindForcedAllIn = true
            // if the big blind is all in, force the small blind to downgrade and match the only bet
            this.doPostBlindForPlayer(smallBlindPlayer.getId(), new Bet(bigBlindBetAmount, BetKind.SMALLBLIND))
            this.doPostBlindForPlayer(bigBlindPlayer.getId(), new Bet(bigBlindBetAmount, BetKind.BIGBLIND))
          } else {
            this.doPostBlindForPlayer(smallBlindPlayer.getId(), new Bet(smallBlindBetAmount, BetKind.SMALLBLIND))
            this.doPostBlindForPlayer(bigBlindPlayer.getId(), new Bet(bigBlindBetAmount, BetKind.BIGBLIND))
          }
        } else {
          this.doPostBlindForPlayer(smallBlindPlayer.getId(), new Bet(smallBlindBetAmount, BetKind.SMALLBLIND))
          this.doPostBlindForPlayer(bigBlindPlayer.getId(), new Bet(bigBlindBetAmount, BetKind.BIGBLIND))
        }
      } else {
        throw new TriedToPlayBlindsOutOfOrderError()
      }
    }
  }

  removeOutPlayers() {
    const eventVersion = this.updateState({ name: TableEventKind.PlayerOut })
    this.emitEvent({ ...TableEvents.playerOutEvent(this), ...eventVersion })
  }

  removeForeverOutPlayers() {
    logger.trace('removeForeverOutPlayers called')
    const foreverOutPlayers: Player[] = this.foreverOutPlayers()
    foreverOutPlayers.map((op) => {
      op.out()
      const eventVersion = this.updateState({ name: TableEventKind.PlayerForeverOut, forPlayerId: op.id })
      this.emitEvent({ ...TableEvents.playerForeverOutEvent(this, op), ...eventVersion })
    })
  }

  resetInactivePlayers() {
    const eventVersion = this.updateState({ name: TableEventKind.PlayerReset })
    this.emitEvent({ ...TableEvents.playerResetEvent(this), ...eventVersion })
  }

  enableAutoStart() {
    this.autostart = true
    const eventVersion = this.updateState({ name: TableEventKind.AutoStart })
    this.emitEvent({ ...TableEvents.autoStartEvent(this), ...eventVersion })
  }

  disableAutoStart() {
    this.autostart = false
    const eventVersion = this.updateState({ name: TableEventKind.AutoStart })
    this.emitEvent({ ...TableEvents.autoStartEvent(this), ...eventVersion })
  }

  /**
   *
   * @returns true if there are not enough players and table is reset
   */
  resetIfNotEnoughPlayers() {
    if (this.activePlayers().length < this.minPlayers) {
      this.reset()
      return true
    }
    return false
  }

  /**
   * Resets the game and returns players to a reset state
   * After calling reset, the button will be placed and
   * players will be waitlisted
   */
  reset() {
    // put players back to waitlist
    this.updateCardsVisibilityOnShowDown(false)

    this.playingPlayers().map((player) => {
      player.chips += player.bet
      player.resetBets()
      player.resetPlayer()
      player.resetLastAction()
      this.waitlistPlayer(player)
    })

    this.buttonPlayerId = ''
    this.boardDealState = ''
    this.state = GameReducer(GameActions.ResetPlay, this.state)
    const eventVersion = this.updateState({ name: TableEventKind.Reset })
    this.emitEvent({ ...TableEvents.tableResetEvent(this), ...eventVersion })
  }

  /**
   * This is a softReset used when autoStart is disabled. This
   * removes players bets, actions, etc, while leaving the player
   * in their seat.
   */
  softReset(shouldEmitEvent = true) {
    // reset players state, but do not waitlist
    logger.trace('[Table.softReset]')

    this.updateCardsVisibilityOnShowDown(false)

    this.gamePlayers().map((player) => {
      player.chips += player.bet
      player.resetBets()
      player.resetPlayer(false)
      player.resetLastAction()
    })

    this.boardDealState = ''
    this.setPlayersLastAction()
    this.state = GameReducer(GameActions.ResetPlay, this.state)
    const eventVersion = this.updateState({ name: TableEventKind.Reset })
    if (shouldEmitEvent) {
      this.emitEvent({ ...TableEvents.tableResetEvent(this), ...eventVersion })
    }
  }

  updatePlayersFromWaitlist() {
    const waitlist = this.getWaitlist()

    while (waitlist.length > 0 && this.playingPlayers().length < this.maxPlayers) {
      const playerId = waitlist.pop().getId()
      const player = this.getPlayerById(playerId)
      if (this.isTournament() && player.doNoActionCount === this.maxDoNoActionCount) {
        player.resetDoNoActionCount()
        logger.trace('[Table.updatePlayersFromWaitlist]', playerId)
      } else {
        const playerIndex = this.getPlayerIndexByPlayerId(playerId)

        // Remove player from existing position
        this.players.splice(playerIndex, 1)

        // Place player behind the button player
        this.players.splice(this.getButtonPlayerIndex(), 0, player)

        // this.board.addPlayerBettingSeatAt(this.getButtonPlayerIndex())
      }
      player.beginPlay()
      const eventVersion = this.updateState({ name: TableEventKind.PlayerJoined, forPlayerId: player.id })
      this.emitEvent({ ...TableEvents.playerJoinedEvent(this, player), ...eventVersion })
    }
  }

  updateHandStrengths() {
    this.updateHandStrengthsForPlayers(this.playingPlayers())
  }

  updateHandStrengthsForPlayers(players: Player[]): Player[] {
    try {
      players.map((player) => {
        player.setRankedHand(this.board.communityCards)
      })
      const eventVersion = this.updateState({ name: TableEventKind.RankedHandEvent })
      this.emitEvent({ ...TableEvents.rankedHandEvent(this), ...eventVersion })
    } catch (err) {
      logger.debug(err)
      logger.debug(this.board.communityCards)
      players.map((player) => {
        logger.debug(player.showCards())
      })
    }
    return players
  }

  updatePlayerActions() {
    logger.trace('[Table.updatePlayerActions]')
    this.playingPlayers().map((player) =>
      player.setActions(ActionEvaluator.getActionHints(this.board, player, this.getBigBlindPlayer()))
    )
  }

  updatePlayingPlayers() {
    this.updateHandStrengths()
    this.updatePlayerActions()

    logger.trace(`[Table.updatePlayingPlayers] currentPlayer ${this.getCurrentPlayer()?.getId()}`)
    logger.trace(`[Table.updatePlayingPlayers] currentPlayer userId ${this.getCurrentPlayer()?.userId}`)
  }

  doShowdown() {
    logger.trace('Doing Showdown')
    this.stackThePots()
    this.gamePlayers().map((player) => {
      player.resetBets()
    })

    let handWinnersEventVersion

    const playerWithChips = this.getPlayersWithChips()
    if (playerWithChips.length === 1) {
      const winner = playerWithChips[0]
      this.finalWinnerInfo = {
        communityCards: [...this.board.communityCards],
        player: new PotWinner(
          winner.id,
          winner.name,
          winner.cards,
          winner.chips,
          PotKind.MAIN,
          winner.rankedHand.name,
          winner.rankedHand?.descr,
          winner.rankedHand?.cards.map((card) => getCardString(card)),
          winner.userId
        )
      }
    }

    if (this.checkEveryoneFolded()) {
      const eventVersion = this.updateState({ name: TableEventKind.HandEveryoneFolded })
      this.emitEvent({ ...TableEvents.handEveryoneFolded(this), ...eventVersion })
      handWinnersEventVersion = eventVersion
    } else {
      this.updateCardsVisibilityOnShowDown(true)
      const eventVersion2 = this.updateState({ name: TableEventKind.HandShowdown })
      this.emitEvent({ ...TableEvents.handShowdownEvent(this), ...eventVersion2 })
      handWinnersEventVersion = eventVersion2
    }

    //TODO: we need to hide cards when showing winners only on supported game types
    this.updateCardsVisibilityOnShowDown(false)
    this.emitEvent({
      ...TableEvents.handWinnersEvent(this),
      ...{ version: handWinnersEventVersion.version + 1, sourceId: handWinnersEventVersion.sourceId }
    })
    this.updateState({ name: TableEventKind.HandWinners })
  }

  stackThePots() {
    logger.trace('stackThePots')
    if (!this.board.isShowdown() && this.inPlayPlayers().length > 1) {
      throw new CannotStackPotsBeforeShowdown()
    }
    if (this.checkEveryoneFolded()) {
      const player =
        this.activePlayers().length === 0
          ? this.getBigBlindPlayer() || this.getSmallBlindPlayer() || this.playingPlayers()[0]
          : this.activePlayers()[0]

      if (!player) {
        throw new Error('Cannot stack pots for missing everyone folded winner.')
      }

      this.board.getAllPots().map((pot) => {
        player.chips += pot.amount
        logger.trace(`stackThePots in map ${player.chips} ${pot.amount}`)
        // create empty pot winner when everyone folded
        pot.setWinners([this.createEmptyPotWinner(player, pot)])
      })
    } else {
      this.board.getAllPots().map((pot) => {
        const potWinners: PotWinner[] = []
        const activePlayerIds = this.activePlayers().map((player) => player.getId())

        let players = pot.players.length > 0 ? pot.players : this.activePlayers()
        players = players.filter((player) => activePlayerIds.includes(player.getId()))
        players = this.updateHandStrengthsForPlayers(players)
        // Tally bets from players that left
        players.map((p) => {
          p.chips += p.bet
          p.resetBets()
        })
        if (players.length === 1) {
          const sidePotPlayer = this.getPlayerById(players[0].getId())
          sidePotPlayer.chips += pot.amount
          potWinners.push(this.createPotWinner(sidePotPlayer, pot.amount, pot.kind))
        } else {
          const winningHands = Hand.winners([
            ...players.filter((player) => player?.rankedHand).map((player) => player.rankedHand)
          ])
          const payout = this.getPayout(pot.amount, winningHands)
          winningHands.map((hand) => {
            const player = this.getPlayerById(hand.playerId)
            let totalPayout = parseInt(payout[0].toString(), 10)

            if (hand.playerId === payout[1]) {
              totalPayout += parseInt(payout[2].toString(), 10)
            }

            const tablePlayer = this.getPlayerById(player.getId())
            tablePlayer.chips += totalPayout

            // logger.info(`${tablePlayer.name} won ${totalPayout} chips from ${pot.kind}`)
            potWinners.push(this.createPotWinner(tablePlayer, totalPayout, pot.kind))
          })
        }
        pot.setWinners(potWinners)
      })
    }
  }

  /**
   * Creates a pot winner for the specified player, payout, and PotKind
   * @param player
   * @param totalPayout
   * @param potKind
   * @returns
   */
  createPotWinner(player: Player, totalPayout: number, potKind: PotKind) {
    if (player && (player.isAllIn() || player.isInPlay())) {
      return new PotWinner(
        player.getId(),
        player.name,
        player.showCards(),
        totalPayout,
        potKind,
        player.rankedHand.name,
        player.rankedHand.descr,
        player.rankedHand.cards.map((card) => getCardString(card)),
        player.userId
      )
    }
    throw new Error('could not create pot winner error')
  }

  /**
   *
   * @param player
   * @param pot
   * @returns
   */
  createEmptyPotWinner(player: Player, pot: Pot) {
    if (
      player &&
      (player.isInPlay() || player.isAllIn() || player.role === 'BB' || player.role === 'SB' || player.isFolded())
    ) {
      return new PotWinner(
        player.getId(),
        player.name,
        [],
        pot.amount,
        pot.kind,
        player.rankedHandName,
        player.rankedHandDescription,
        [...this.board.communityCards],
        player.userId
      )
    }
    throw new Error('could not create pot winner error')
  }

  /**
   * Payouts that divide equally are distributed as is.
   * Payouts that do not divide equally should:
   *  1. Round down all values
   *  2. Distribute the remainder (1 chip) to the player
   *     that is closest to the button
   *
   * @param potAmount total pot amount
   * @param winningHands array of winning hands from solver
   * @returns [payout amount to be used, seat to receive remainder, remainder ]
   */
  getPayout(potAmount: number, winningHands: PotWinner[]) {
    const payout = potAmount / winningHands.length
    if (Number.isInteger(payout)) {
      return [payout, '', 0]
    }
    const buttonPlayerSeat = this.getButtonPlayerIndex()

    //find the seat closest to the button
    const closestToButton = winningHands.reduce(
      (acc, hand) => {
        const playerSeat = this.getPlayerIndexByPlayerId(hand.playerId)
        const seatDiff = playerSeat - buttonPlayerSeat
        if (acc && seatDiff < acc[0]) {
          return [seatDiff, hand.playerId]
        }
        return acc
      },
      [0, '']
    )

    return [Math.floor(payout), closestToButton[1], 1]
  }

  captureBets() {
    this.adjustBetForBigBlind()
    this.resetLeftPlayerBets()
    const onlyOnePlayerAllIn = this.playingPlayers().length - this.allInPlayers().length
    if (this.allInPlayers().length === 0 || (onlyOnePlayerAllIn === 0 && this.allInPlayers().length === 1)) {
      /** There were no all-in players or side-pots to evaluate */
      this.board.addBetsToPot()
    } else {
      let i = 0
      logger.debug(`[Table.captureBets(${i})] this.activePlayers().length ${this.activePlayers().length}`)
      while (this.board.hasNonZeroBets() && i < this.activePlayers().length) {
        let smallestBet = this.getSmallestBet()
        logger.debug(`[Table.captureBets(${i})] smallestBet ${smallestBet}`)

        // At least one player is allIn
        if (this.allInPlayers().length > 0) {
          logger.debug(`[Table.captureBets(${i})] this.allInPlayers().length ${this.allInPlayers().length}`)
          const bets = this.allInPlayers().map((item) => item.bet)
          if (bets.length > 0) {
            const min = Math.min.apply(null, bets)
            logger.debug(`[Table.captureBets(${i})] min bet ${min}`)
            if (min !== 0) {
              smallestBet = min
            }
          }
        }
        const sidePot = this.activePlayers()
          .filter((player) => player.bet > 0)
          .reduce(
            (pot, player) => {
              // reduce the bets for players and update bet amount
              const playerSeat = this.getPlayerIndexByPlayerId(player.getId())
              this.board.adjustBetForPlayerAt(playerSeat, smallestBet)
              logger.debug(`[Table.captureBets(${i})] sidePot loop player.name ${player.name} ${player.role}`)
              logger.debug(`[Table.captureBets(${i})] sidePot loop player.chips ${player.chips}`)
              logger.debug(`[Table.captureBets(${i})] sidePot loop player.bet ${player.bet}`)
              logger.debug(`[Table.captureBets(${i})] sidePot loop smallestBet ${smallestBet}`)
              logger.debug(`[Table.captureBets(${i})] sidePot loop pot.amount ${pot.amount}`)

              player.bet -= smallestBet
              // add smallest bet amount to sidepot with players
              pot.amount += smallestBet

              pot.players.push(player)

              return pot
            },
            new Pot(0, [], PotKind.SIDE)
          )

        logger.debug(`[Table.captureBets(${i})] sidePot.amount ${sidePot.amount}`)
        logger.debug(`[Table.captureBets(${i})] sidePot.players.length ${sidePot.players.length}`)

        // If sit out player is SB and BB
        const sitOurPlayerWithBets = this.getSitOutPlayers().filter((p) => p.bet > 0)
        if (sitOurPlayerWithBets.length > 0) {
          sidePot.amount += this.adjustLeftAmountInSidePot(smallestBet)
        }

        // @TODO: Is there a way to merge all this logic with less if, else statements?
        if (sidePot.players.length === 1) {
          // player went all in or over bet
          const player = this.getPlayerById(sidePot.players[0].getId())
          if (!player) {
            throw new Error('Cannot capture bets for missing sidepot player')
          }

          player.chips += sidePot.amount
          if (player.bet > sidePot.amount) {
            player.chips += player.bet
          }
          player.bet = 0
          const playerSeat = this.getPlayerIndexByPlayerId(player.getId())
          this.board.adjustBetForPlayerAt(playerSeat, 0)
        } else {
          if (sidePot.amount !== 0 || sidePot.players.length !== 0) {
            if (sidePot.players.length === this.activePlayers().length) {
              sidePot.kind = PotKind.MAIN
            }
            this.board.addPot(sidePot)
          }
        }
        i++
      }
      this.board.clearBets()
    }
  }

  resetLeftPlayerBets() {
    this.leftPlayers.map((player) => {
      if (player.bet > 0 || player.bets.length > 0) {
        player.resetBets()
      }
    })
  }

  adjustLeftAmountInSidePot(smallestBet: number) {
    const sitOurPlayerWithBets = this.getSitOutPlayers().filter((p) => p.bet > 0)
    const sitOutPlayerTotalBets = sitOurPlayerWithBets.reduce((sum, player) => {
      if (player.bet >= smallestBet) {
        player.bet -= smallestBet
        sum += smallestBet
        player.chips += player.bet
      } else {
        sum += player.bet
      }
      player.resetBets()
      return sum
    }, 0)
    return sitOutPlayerTotalBets
  }

  adjustBetForBigBlind() {
    if (this.getBigBlindPlayer()) {
      const playerseat = this.getPlayerIndexByPlayerId(this.getBigBlindPlayer().getId())
      const updateBet = this.getBigBlindPlayer().bet
      this.board.adjustBetForBigBlind(playerseat, updateBet)
    }
  }

  getSmallestBet() {
    if (this.getSmallBlindPlayer()?.isFolded() && this.getBigBlindPlayer()?.isFolded()) {
      logger.trace('[Table.getSmallestBet] smallblind player and bigblind player smallbet')
      return this.board.smallestBetWithoutSBandBB()
    }
    if (this.getSmallBlindPlayer()?.isFolded()) {
      logger.trace('[Table.getSmallestBet] smallblind player smallbet')
      return this.board.smallestBetWithoutSB()
    }
    if (this.getBigBlindPlayer()?.isFolded()) {
      logger.trace('[Table.getSmallestBet] bigblind player smallbet')
      return this.board.smallestBetWithoutBB()
    }
    logger.trace('[Table.getSmallestBet] smallest bet no players folded')
    return this.board.smallestBet()
  }

  checkBettingWithAllIn() {
    const unequalBets = this.activePlayers().reduce((acc, player) => {
      if (!acc) {
        acc = player.bet !== this.board.biggestBet()
      }
      return acc
    }, false)

    if (this.allPlayersAreAllIn() && unequalBets) {
      return true
    }

    for (let i = 0; i < this.activePlayers().length; i++) {
      if (this.activePlayers()[i].bets.length === 0) {
        return true
      }
    }

    /** check all-in players for bets less than the biggest bet */
    const biggestBet = this.board.biggestBet()
    const biggestAllIn = this.allInPlayers().reduce((acc, allInPlayer) => {
      if (allInPlayer.bet > acc) {
        acc = allInPlayer.bet
      }
      return acc
    }, 0)

    return biggestBet - biggestAllIn > 0
  }

  playersHaveFinishedBetting() {
    logger.trace('[Table.playersHaveFinishedBetting]')
    const biggestBet = this.board.biggestBet()
    logger.trace(`[Table.playersHaveFinishedBetting] biggestBet: ${biggestBet}`)
    const stillBetting = this.activePlayers().reduce((acc, player) => {
      if (player.isAllIn()) {
        return acc
      }

      const bet = this.board.getBetForPlayerAt(this.getPlayerIndexByPlayerId(player.getId()))
      const isBlankBet = bet && bet.kind === BetKind.BLANK
      if (bet && !isBlankBet && (bet.amount !== biggestBet || bet.kind === BetKind.BIGBLIND)) {
        logger.trace(
          `[Table.playersHaveFinishedBetting] ${player.name} at seat ${player.getSeatId()} ${bet.kind} ${bet.amount}`
        )
        return acc + 1
      }
      if (!bet || isBlankBet) {
        logger.trace(`[Table.playersHaveFinishedBetting] ${player.name} at seat ${player.getSeatId()} no bet!]`)
        return acc + 1
      }

      return acc
    }, 0)

    logger.trace(`[Table.playersHaveFinishedBetting] is finished betting ${stillBetting === 0}`)
    if (this.isSmallOrBigBlindForcedAllIn) {
      return true
    }

    return stillBetting === 0
  }

  getBetHints() {
    return BettingRecommender.getBetHints(this.board)
  }

  checkEveryoneFolded() {
    if (this.foldedPlayers().length > 0 && this.activePlayers().length === 0) {
      return true
    }
    return this.foldedPlayers().length === this.playingPlayers().length - 1
  }

  getSitOutPlayers() {
    return this.players.filter((p) => p.doNoActionCount === this.maxDoNoActionCount && p.chips > 0)
  }

  isHeadsUpPlay() {
    return this.playingPlayers().length === 2 && this.getSitOutPlayers().length === 0
  }

  isPaused() {
    return GameStates.isPaused(this.state)
  }

  isSleep() {
    return GameStates.isSleep(this.state)
  }

  isPlaying() {
    return GameStates.isPlaying(this.state)
  }

  isReadyToPlay() {
    return GameStates.isReadyToPlay(this.state)
  }

  isReset() {
    return GameStates.isReset(this.state)
  }

  isFinished() {
    return GameStates.isFinished(this.state)
  }

  isDefault() {
    return GameModes.isDefault(this.mode)
  }

  isTournament() {
    return GameModes.isTournament(this.mode) || GameModes.isPracticeTournament(this.mode)
  }

  isPracticeTournament() {
    return GameModes.isPracticeTournament(this.mode)
  }

  isPractice() {
    return GameModes.isPractice(this.mode)
  }

  isOpenTable() {
    return GameModes.isOpenQuickGame(this.mode)
  }

  isThreeCard() {
    return GameModes.isThreeCard(this.mode)
  }

  onPokerTableCreated() {
    logger.trace('OnPokerTableCreated called')
    logger.debug(
      `OnPokerTableCreated ${this.name} ${this.minPlayers} ${this.maxPlayers} ${this.bigBlind} ${this.button} ${this.currentPlayer}`
    )
    this.setId(this.id)

    this.blinds = new Blind(this.bigBlind)
    this.board = new Board(this.blinds)
  }

  onPokerTableStarted() {
    logger.trace('OnPokerTableStarted called')
    this.state = GameReducer(GameActions.StartPlay, this.state)
  }

  onPokerTablePaused() {
    logger.trace('OnPokerTablePaused called')
    this.state = GameReducer(GameActions.PausePlay, this.state)
  }

  onPokerTableResumed() {
    logger.trace('OnPokerTableResumed called')
  }

  onPokerTableSleep() {
    logger.trace('OnPokerTableSleep called')
    this.state = GameReducer(GameActions.SleepPlay, this.state)
  }

  onPokerTableReset() {
    logger.trace('OnPokerTableReset called')
    this.state = GameReducer(GameActions.ResetPlay, this.state)
  }

  onPokerTableFinished() {
    logger.trace('OnPokerTableFinished called', this.state)
  }

  onPokerTableHandNew() {
    logger.trace('OnPokerTableHandNew called')

    // Reset values
    this.bigBlindFold = false
    this.bigBlindLeft = false
    this.smallBlindFold = false
    this.smallBlindLeft = false
    this.isDeadSmallBlindNextHand = false
    this.boardDealState = ''

    if (this.blinds.big === 0) {
      this.blinds = new Blind(TABLE_CONFIG.DEFAULT_BIG_BLIND)
    }

    if (this.updatedCurrentBlinds.big > 0 && this.updatedCurrentBlinds.big > this.blinds.big) {
      this.blinds = this.updatedCurrentBlinds
    }

    if (this.playingPlayers().length === 0 && this.getWaitlist().length === 0) {
      return
    }

    // check big blind out
    this.checkIfBigBlindWentOut()

    // remove out players
    this.removeOutPlayers()

    // forever out players
    // this.removeForeverOutPlayers()

    //reset players from fold / allin
    this.resetInactivePlayers()

    //check waitlist and update players
    this.updatePlayersFromWaitlist()

    this.updatePlayersSeat()

    //reset players
    this.collectCards()

    // 1 player is waiting or still in, but 1+ sitting out players, the game has to go on
    if (
      (!(this.isTournament() || this.isTournamentFinished()) && this.resetIfNotEnoughPlayers()) ||
      !this.isLastPlayerStanding()
    ) {
      return
    }
    // if (this.resetIfNotEnoughPlayers()) {
    //   return
    // }

    //reset board
    this.board.setupNewHand(this.blinds, { cards: this.threeCardDeck, clearCommunityCards: true })

    if (this.activePlayers().length < this.minPlayers) {
      this.board.setPlayers(this.getPlayersWithChips())
    } else {
      this.board.setPlayers(this.activePlayers())
    }

    this.setTheButton()

    // reset bets and pay blinds
    this.gamePlayers().map((p) => p.resetBets())

    this.payBlinds()

    this.board.nextAction()
    this.dealCards()

    this.updatePlayingPlayers()

    // Reset lastAction when game ends
    this.players.map((p) => p.resetLastAction())

    this.setPlayersLastAction()

    const eventVersion = this.updateState({ name: TableEventKind.Started })
    this.emitEvent({ ...TableEvents.tableStartedEvent(this), ...eventVersion })

    // if we only have 2 players, and 1 player is forced all-in because of the blind levels, then match the bet and end let the hand play out
    if (this.isSmallOrBigBlindForcedAllIn) {
      // this.doCheckForPlayer(this.getCurrentPlayer().getId()) // check for the small blind
      // this.doCheckForPlayer(this.getCurrentPlayer().getId()) // check for the big blind
      this.nextTurn()
    } else {
      logger.debug('OnPokerTableHandNew update handCurrentPlayerEvent')
      const eventVersion2 = this.updateState({
        name: TableEventKind.HandCurrentPlayer,
        forPlayerId: this.getCurrentPlayer().id
      })
      this.emitEvent({ ...TableEvents.handCurrentPlayerEvent(this, this.getCurrentPlayer()), ...eventVersion2 })
    }
  }

  updatePlayersSeat() {
    this.players.map((player) => {
      const seatId = this.getPlayerIndexByPlayerId(player.getId())
      this.board.addPlayerBettingSeatAt(seatId)
      player.setSeatId(seatId)
    })
  }

  onPokerTableHandCurrent_player(event: EventHandler) {
    logger.trace('OnPokerTableHandCurrent_player called')
    if (event.forPlayerId) {
      const eventCurrentPlayerId = event.forPlayerId
      if (this.getCurrentPlayer().getId() !== eventCurrentPlayerId) {
        this.currentPlayer = this.getPlayerIndexByPlayerId(eventCurrentPlayerId)
        this.currentPlayerId = eventCurrentPlayerId
      }
    }
    this.updatePlayingPlayers()
  }

  onPokerTableHighestBet_player() {
    logger.trace('onPokerTableHighestBet_player called')
  }

  onPokerTableHandDealt() {
    logger.trace('OnPokerTableHandDealt called')
    this.players.map((eventPlayer) => {
      // const eventPlayer = event.snapshot.players[index]
      const player = this.getPlayerById(eventPlayer.id)
      if (JSON.stringify(player.showCards()) !== JSON.stringify(eventPlayer.cards)) {
        player.returnCards()
        player.getCards(eventPlayer.cards)
      }
    })
  }

  processOnPlayerJoined() {
    if (GameModes.isTournament(this.mode) && !this.isTableFull && this.checkTableIsFull()) {
      this.isTableFull = true
    }

    if (this.autostart && this.isReset() && this.activePlayers().length >= this.minPlayers) {
      /**
       * Board is in a pre-deal state, we've just added a player and
       * potentially other players from the waitlist, check length
       * of active players and move board to Deal via newHand if
       * the game has already started/"isPlaying"
       */
      logger.trace('onPokerTablePlayerJoined - calling new hand')
      this.state = GameReducer(GameActions.StartPlay, this.state)
      this.newHand()
    }
  }

  onPokerTablePlayerJoined() {
    logger.trace('[Table.onPokerTablePlayerJoined] called')
    this.processOnPlayerJoined()
  }

  onPokerTableBotsManuallyAdded() {
    logger.trace('[Table.onPokerTableBotsManuallyAdded] called')
    this.processOnPlayerJoined()
  }

  onPokerTablePlayerWaitlisted(event: EventHandler) {
    logger.trace('onPokerTablePlayerWaitlisted called')

    if (!this.getPlayerById(event.forPlayerId, true)) {
      const forPlayer = this.getPlayerById(event.forPlayerId)
      this.players.splice(this.getButtonPlayerIndex(), 0, forPlayer)
    }
    if (GameModes.isTournament(this.mode) && !this.isTableFull && this.checkTableIsFull()) {
      this.isTableFull = true
    }
  }

  onPokerTablePlayerOut() {
    logger.trace('onPokerTablePlayerOut called')
    const outPlayers = this.outPlayers()
    outPlayers.map((op) => {
      op.resetPlayer()
      op.out()
      if (op.buyIn && !op.isObserver()) {
        // op.playerOutTime = this.currentCheckpoint()
        const eventVersion = this.updateState({ name: TableEventKind.PlayerOutOfChips, forPlayerId: op.id })
        this.emitEvent({ ...TableEvents.playerOutOfChipsEvent(this, op), ...eventVersion })
      }
    })
  }

  onPokerTablePlayerForeverout() {
    logger.trace('onPokerTablePlayerForeverout called')
  }

  onPokerTablePlayerOutofchips() {
    logger.trace('onPokerTablePlayerOutOfChips called')
  }

  onPokerTablePlayerLeft() {
    const numberOfPlayers = this.gamePlayers().length

    if (numberOfPlayers === 1) {
      this.board.communityCards = []
    }

    logger.trace('onPokerTablePlayerLeft called')
  }

  onPokerTablePlayerDisconnected() {
    logger.trace('onPokerTablePlayerDisconnected called')
  }

  onPokerTablePlayerObserver() {
    logger.trace('onPokerTablePlayerObserver called')
  }

  onPokerTablePlayerBet(event: EventHandler) {
    logger.trace('[Table.onPokerTablePlayerBet] called')
    const bet = event.withBet

    if (typeof bet === 'undefined') {
      throw new BetUndefinedForPlayer()
    }

    const player = this.getPlayerById(event.forPlayerId)
    const playerSeat = this.getPlayerIndexByPlayerId(player.getId())
    const minimumRaise = this.getMinimumRaise()

    // TODO: WHY DO WE NEED THIS??
    // /**
    //  * Identify if this bet is a raise
    //  */
    if ([BetKind.BET].includes(bet.kind)) {
      if (this.board.biggestBet() > 0 && bet.amount > this.board.biggestBet() && !player.isAllIn()) {
        bet.kind = BetKind.RAISE
      }
    }

    /**
     * Edge case: When going through first betting round, if every player just calls BB, but BB wants to raise,
     * BB player would just be raising their BB bet. So in that case, assume player has not bet chips yet.
     */
    const isPlayerBB = player.role === 'BB'
    const isDeal = this.board.isDeal()
    const playersOnlyCalledBB = this.board.playersOnlyCalledBB
    const currentPlayerOnlyPlacedBB = player.bets.filter(Boolean).every((b) => b._kind === BetKind.BIGBLIND)
    const isBBRaisingThemselves =
      isPlayerBB && isDeal && bet.kind === BetKind.RAISE && playersOnlyCalledBB && currentPlayerOnlyPlacedBB

    if (player.bet && bet.kind !== BetKind.CALL) {
      // logger.trace(`[Table.onPokerTablePlayerBet] playerBet ${playerAlreadyBet.kind} ${playerAlreadyBet.amount}`)

      // Amount player needs to add to previous bet to match desired bet.
      bet.amount = Math.abs(bet.amount - player.bet)
    }

    const playerAlreadyBetAmount = isBBRaisingThemselves ? 0 : player.bet
    const isValidBet = BettingValidator.isValidBetForPlayer(
      {
        bigBlind: this.blinds.big,
        biggestBet: this.board.biggestBet(),
        playerAlreadyBet: playerAlreadyBetAmount,
        playerChips: player.chips,
        betAmount: bet.amount,
        callAmount: this.callAmount,
        minimumRaise: isBBRaisingThemselves ? this.blinds.big : minimumRaise
      },
      bet.kind
    )

    if (!isValidBet.ok) {
      logger.error(
        JSON.stringify({
          bet,
          bigBlind: this.blinds.big,
          biggestBet: this.board.biggestBet(),
          playerAlreadyBet: playerAlreadyBetAmount,
          playerChips: player.chips,
          betAmount: bet.amount,
          callAmount: this.callAmount,
          minimumRaise: isBBRaisingThemselves ? this.blinds.big : minimumRaise
        })
      )
      throw new Error(`Bet Validation Error: ${isValidBet.error}. ${JSON.stringify(isValidBet.info)}`)
    }

    player.payBet(bet)

    if (player.isAllIn()) {
      const eventVersion = this.updateState({ name: TableEventKind.PlayerAllIn, forPlayerId: event.forPlayerId })
      this.emitEvent({ ...TableEvents.playerAllInEvent(this, player), ...eventVersion })
    }

    logger.trace(`[Table.onPokerTablePlayerBet] player.bet ${player.name} ${player.bet}`)
    this.board.doBetForPlayerAt(playerSeat, new Bet(player.bet, bet.kind))
  }

  downGradeBets() {
    const players = this.allInPlayers()
    if (players.length > 0) {
      const bets = players.map((item) => item.bet)
      if (bets.length > 0) {
        const max = Math.max.apply(null, bets)
        let secondMax = 0
        if (bets.length > 1) {
          bets.splice(bets.indexOf(max), 1)
          secondMax = Math.max.apply(null, bets)
        } else {
          secondMax = bets[0]
        }
        if (secondMax === 0) {
          secondMax = max
        }
        if (secondMax > 0) {
          this.activePlayers().forEach((player) => {
            if (player.bet > secondMax) {
              const index = this.getPlayerIndexByPlayerId(player.id)
              this.players[index].downGradeAmount = secondMax
              this.players[index].downGradeState = PlayerStates.InPlay.tag
            }
          })
        }
      }
    }
  }

  /**
   * For reference see {@link file://./../docs/minimumRaise.drawio.svg}
   */
  getMinimumRaise() {
    const biggestBet = this.board.biggestBet()
    const secondBiggestBet = this.board.secondBiggestBet()
    const differenceBetweenBiggestAndSecondBiggestBet = biggestBet - secondBiggestBet
    let minRaise = 0
    if (this.isPreFlop()) {
      if (biggestBet === this.blinds.big) {
        minRaise = this.blinds.big * 2
      } else {
        minRaise = biggestBet + differenceBetweenBiggestAndSecondBiggestBet
      }
    } else {
      if (biggestBet <= 0) {
        minRaise = this.blinds.big
      } else if (secondBiggestBet <= 0) {
        minRaise = biggestBet * 2
      } else {
        minRaise = biggestBet + differenceBetweenBiggestAndSecondBiggestBet
      }
    }

    // Minimum Raise can never be less than big blind.
    if (minRaise >= this.blinds.big) return minRaise
    return this.blinds.big
  }

  /**
   * For reference see {@link file://./../docs/callAmount.drawio.svg}
   */
  get callAmount() {
    const player = this.getCurrentPlayer()
    const biggestBet = this.board.biggestBet()

    if (!player) {
      if (GameStates.isPlaying(this.state)) {
        // throw new Error('Missing player information to calculate callAmount')
        logger.error('Missing player information to calculate callAmount')
        return undefined
      }
      return 0
    }

    if (!biggestBet) {
      if (biggestBet === 0) {
        return 0
      }
      throw new Error('Missing biggestBet information to calculate callAmount')
    }

    // If biggest bet is less than blinds (some player had less chips than bb and went all in), callAmount still is bb.
    const callAmount = this.blinds.big >= biggestBet && this.inPlayPlayers().length > 0 ? this.blinds.big : biggestBet

    // But if current player has less chips than the call amount, their call amount is all in amount.
    if (callAmount >= player.chips + player.bet) return player.chips

    // If not all in, call amount for current player is the difference needed to add to match biggest bet/raise or BB.
    return callAmount - player.bet
  }

  isPreFlop() {
    return !(this.board.isPreDeal() || this.board.isFlop() || this.board.isTurn() || this.board.isRiver())
  }

  onPokerTablePlayerAllin(event: EventHandler) {
    logger.trace('onPokerTablePlayerAllin called')
    const player = this.getPlayerById(event.forPlayerId)
    player.goAllIn()
  }

  onPokerTablePlayerChecked(event: EventHandler) {
    logger.trace('[Table.onPokerTablePlayerChecked] called')

    logger.trace(`[Table.onPokerTablePlayerChecked] ${JSON.stringify(this.board.state)}`)

    const player = this.getPlayerById(event.forPlayerId)
    this.board.doCheckForPlayerAt(this.getPlayerIndexByPlayerId(player.getId()))
    player.payBet(new Bet(0, BetKind.CHECK))
  }

  onPokerTablePlayerCalled(event: EventHandler) {
    logger.trace('onPokerTablePlayerCalled called')
    const playerId = event.forPlayerId
    const biggestBet = this.board.biggestBet()

    if (biggestBet === 0) {
      throw new CantCallNoBetError()
    }

    this.doBetForPlayer(playerId, new Bet(this.callAmount, BetKind.CALL))
  }

  onPokerTablePlayerFolded(event: EventHandler) {
    logger.trace('onPokerTablePlayerFolded')
    const player = this.getPlayerById(event.forPlayerId)
    const seatId = this.getPlayerIndexByPlayerId(player.getId())
    // @TODO: do we need to check for seatId === -1 if the player is not found???
    const foldedBet = this.board.getBetForPlayerAt(seatId)

    // Player is folding, so take all the bets on the table and add them to the pot
    if (foldedBet && foldedBet.amount !== 0 && foldedBet.kind !== BetKind.BLANK) {
      const smallestBet = this.getSmallestBet()
      // check if minimum bet amount < blind amount
      if (
        (smallestBet < foldedBet.amount && player.getId() === this.getSmallBlindPlayer()?.getId()) ||
        (this.isPreFlop() && player.getId() === this.getBigBlindPlayer()?.getId())
      ) {
        this.board.pot.amount += smallestBet
        foldedBet.amount -= smallestBet
        const finalBet = foldedBet.amount
        player.chips += finalBet
      } else {
        this.board.pot.amount += foldedBet.amount
      }
      this.board.doBetForPlayerAt(seatId, undefined)
    }
    player.fold()
  }

  onPokerTablePlayerPost_blind(event: EventHandler) {
    logger.trace('onPokerTablePlayerPost_blind called')
    const playerId = event.forPlayerId
    const player = this.getPlayerById(playerId)
    const playerSeat = this.getPlayerIndexByPlayerId(playerId)
    const bet = event.withBet

    player.payBet(bet)
    this.board.doBetForPlayerAt(playerSeat, bet)

    if (player.chips <= 0 && player.doNoActionCount !== this.maxDoNoActionCount) {
      player.goAllIn()
    }
  }

  onPokerTableTimer_started() {
    this.startTimerCheckpoint = this.getCurrentCheckpoint()
    logger.trace('onPokerTableTimer_started called')
  }

  onPokerTablePlayerReset() {
    logger.trace('onPokerTablePlayerReset called')
    this.players.filter((p) => p.isFolded() || p.isAllIn()).map((p) => p.beginPlay())
  }

  private processPlayerAfterBuyChips(playerId: string) {
    const player = this.getPlayerById(playerId)
    player.playerOutTime = 0
    if (player.isOut()) {
      const outPlayer = this.getOutPlayerById(player.getId())
      outPlayer.chips = player.chips
      this.setPlayerDoNoActionCount(player.id)
      this.waitlistPlayer(player)
    }
  }

  onPokerTableBuyInForAllPlayers(event: EventHandler) {
    logger.trace('onPokerTableBuyInForAllPlayers called')
    const players = this.players.filter((player) => canReceiveChips(player.state))
    players.forEach((player) => {
      this.processPlayerAfterBuyChips(player.getId())
    })
  }

  onPokerTablePlayerBuyin(event: EventHandler) {
    logger.trace('onPokerTablePlayerBuyIn called')
    this.processPlayerAfterBuyChips(event.forPlayerId)
  }

  onPokerTablePlayerDoNoActionForPlayerEvent() {
    logger.trace('onPokerTablePlayerDoNoActionForPlayerEvent called')
  }

  onPokerTableHandShowdown() {
    logger.trace('onPokerTableHandShowdown called')
  }

  onPokerTableHandWinners() {
    logger.trace('onPokerTableHandWinners called')

    const tournamentIsFinished = this.isTournament() && this.getPlayersWithChips() <= 1
    // const threeCardIsFinished = GameModes.isThreeCard(this.mode) && this.amountOfHands === THREE_CARD_AMOUNT_OF_HANDS

    if (tournamentIsFinished) {
      this.finishTable()
    } else {
      this.board.nextAction()
    }
  }

  onPokerTableHandAll_players_allin() {
    logger.trace('onPokerTableHandAllPlayersAllIn')
  }

  onPokerTableHandEveryone_folded() {
    logger.trace('onPokerTableHandEveryone_folded')
  }

  onPokerTableStreetFinished() {
    logger.trace('onPokerTableStreetFinished')
  }

  onPokerTablePlayerSitout() {
    logger.trace('onPokerTablePlayerSitout called')
  }

  onPokerTablePlayerShowCardsForTeacherEvent() {
    logger.trace('onPokerTablePlayerShowCardsForTeacherEvent called')
  }

  onPokerTablePlayerShowCardsForEveryoneEvent() {
    logger.trace('onPokerTablePlayerShowCardsForEventEvent called')
  }

  onPokerTablePlayerShowPlayerCardsForTeacherEvent() {
    logger.trace('onPokerTablePlayerShowPlayerCardsForTeacherEvent called')
  }

  onPokerTablePlayerAddChips() {
    logger.trace('onPokerTablePlayerAddChips called')
  }

  onPokerTablePlayerHideCardsForTeacherEvent() {
    logger.trace('onPokerTablePlayerShowCardsForTeacherEvent called')
  }

  onPokerTablePlayerShowPlayerCardsForEveryoneEvent() {
    logger.trace('onPokerTablePlayerShowPlayerCardsForEveryoneEvent called')
  }

  onPokerTablePlayerHideCards() {
    logger.trace('onPokerTablePlayerHideCards called')
  }

  onPokerTablePlayerHidePlayerCards() {
    logger.trace('onPokerTablePlayerHidePlayerCards called')
  }

  onPokerTablePlayerCaptureLastAction() {
    logger.trace('onPokerTablePlayerCaptureLastAction called')
  }

  onPokerTableAutoStart() {
    logger.trace('onPokerTableAutoStart called')
  }

  onPokerTablePlayerRankedHandEvent() {
    logger.trace('onPokerTablePlayerRankedHandEvent called')
  }

  onPokerTableCalculateNextBlindEvent() {
    logger.trace('onPokerTableCalculateNextBlindEvent called')
  }

  onPokerTableTournamentBlindEvent() {
    logger.trace('onPokerTableTournamentBlindEvent called')
  }

  onPokerTablePlayerEndofturn() {
    logger.trace('onPokerTablePlayerEndofturn called')
  }

  onPokerTablePlayerRemovedByTeacherEvent() {
    logger.trace('onPokerTablePlayerRemovedByTeacherEvent called')
  }

  onPokerTablePlayerSetBuyInEvent() {
    logger.trace('onPokerTablePlayerSetBuyInEvent called')
  }

  onPokerTablePlayerCardsVisibleOnShowDownEvent() {
    logger.trace('onPokerTablePlayerCardsVisibleOnShowDownEvent called')
  }

  emitEvent(event: TableEvent) {
    this.event$.next(event)
  }

  getId() {
    return super.getId()
  }

  getVersion() {
    return super.getVersion()
  }

  getEvents() {
    return super.getEvents()
  }

  internalState() {
    return this.state
  }

  getDisconnectedList() {
    return this.players.filter((p) => p.isDisconnected())
  }

  doLeaveGameForPlayer(playerId: string, terminatingGame?: boolean) {
    logger.trace(`doLeaveGameForPlayer playerId ${playerId}`)
    logger.trace(`doLeaveGameForPlayer terminatingGame ${terminatingGame}`)

    const player = this.getPlayerById(playerId)
    // Calculate button and blind position changes before removal
    if (this.getBigBlindPlayer()?.getId() === player.getId()) {
      this.bigBlindLeft = true
    }
    if (this.getSmallBlindPlayer()?.getId() === player.getId()) {
      this.smallBlindLeft = true
    }

    if (this.isPlaying() && this.isCurrentPlayer(playerId) && (player.isInPlay() || player.isAllIn())) {
      if (this.activePlayers().length > 2) {
        this.doFoldForPlayer(playerId, terminatingGame, false)
      }
    } else {
      player.returnCards()
    }

    if (
      (this.isPlaying() || this.isReset() || this.isFinished()) &&
      (player.isInPlay() ||
        player.isFolded() ||
        player.isAllIn() ||
        player.isOut() ||
        player.isWaiting() ||
        player.isSittingOut())
    ) {
      this.leftPlayers.push(player)
      this.doDisconnectForPlayer(playerId)
    }

    this.players.splice(this.players.indexOf(player), 1)

    // If 10TH seat observer is leaving
    if (player.isTeachingObserver()) {
      const observers = this.observerPlayers().filter((p) => p.userRoles.indexOf('teacher') !== -1)
      if (observers.length > 0) {
        const nextObserverId = observers[0].getId()
        this.players = this.players.map((player) => {
          if (player.getId() === nextObserverId) {
            player.observerId = 1
            return player
          }
          return player
        })
      }
    }

    this.resetPlayerSeats()

    /**
     * Remove this player from any pot they were on, they will be filtered
     * for payout already, but this updates current state pot players
     */
    this.board.getAllPots().map((pot) => {
      pot.players = pot.players.filter((player) => player.getId() !== playerId)
    })

    this.adjustTheButton()

    const tableReset = this.isLastPlayerStanding() ? true : this.resetIfNotEnoughPlayers()
    if (
      !tableReset &&
      this.players.length >= this.minPlayers &&
      this.isCurrentPlayer(playerId) &&
      this.activePlayers().length >= 1
    ) {
      this.nextPlayer()
    }

    if (this.shouldForceGameOver()) {
      this.checkGameOver()
    }

    const eventVersion = this.updateState({ name: TableEventKind.PlayerLeft })
    this.emitEvent({ ...TableEvents.playerLeftEvent(this), ...eventVersion })

    // Check SB and BB sitout player leave game
    if (this.checkEveryoneFolded() && this.isPlaying()) {
      this.nextTurn()
    }

    this.completeEvents()
  }

  finishTable() {
    this.outAllGamePlayers()
    this.state = GameReducer(GameActions.EndGame, this.state)
    const eventVersion = this.updateState({ name: TableEventKind.Finished })
    this.emitEvent({ ...TableEvents.tableFinishedEvent(this), ...eventVersion })
  }

  doDisconnectForPlayer(playerId: string) {
    logger.trace('doDisconnectForPlayer', playerId)
    const player = this.getPlayerById(playerId)
    player.disconnect()
    const eventVersion = this.updateState({ name: TableEventKind.Disconnected, forPlayerId: playerId })
    this.emitEvent({ ...TableEvents.disconnected(this, player), ...eventVersion })
  }

  playerIsDisconnected(player: Player) {
    logger.trace(`checkDisconnectedPlayer-player.getId(${player.getId()})`)
    return this.getDisconnectedList().find((p) => p.getId() === player.getId())
  }

  doNoActionForPlayer(forPlayerId: string) {
    try {
      logger.trace(`doNoActionForPlayer forPlayerId ${forPlayerId}`)
      logger.info(forPlayerId)
      logger.trace(`doNoActionForPlayer forPlayerId ${forPlayerId}`)
      if (this.isPaused()) {
        logger.trace('doNoActionForPlayer ignoring, game is paused')
      } else if (this.isPlaying()) {
        if (this.canPlayerTakeAction(forPlayerId)) {
          const player = this.getPlayerById(forPlayerId)
          player.doNoActionCount++
          player.setUserTimedOut(true)

          logger.trace(`doNoActionForPlayer player.doNoActionCount(${player.doNoActionCount})`)

          if (this.isTournament() && player.doNoActionCount === this.maxDoNoActionCount) {
            player.sitOut()
            const eventVersion = this.updateState({ name: TableEventKind.PlayerSitOut, forPlayerId })
            this.emitEvent({ ...TableEvents.playerSitOutEvent(this, player), ...eventVersion })
            this.doFoldForPlayer(player.getId())
            return
          }
          if (player.doNoActionCount === this.maxDoNoActionCount) {
            player.foreverOut()
            player.resetDoNoActionCount()
            this.doFoldForPlayer(player.getId())
            const eventVersion2 = this.updateState({ name: TableEventKind.PlayerForeverOut, forPlayerId })
            this.emitEvent({ ...TableEvents.playerForeverOutEvent(this, player), ...eventVersion2 })
            return
          }

          if (player.canCheck()) {
            logger.trace('doNoActionForPlayer checking for player')
            this.doCheckForPlayer(player.getId())
          } else if (player.canFold()) {
            logger.trace('doNoActionForPlayer folding for player')
            this.doFoldForPlayer(player.getId())
          }
        } else {
          logger.debug(
            "doNoActionForPlayer no longer current player's turn, perhaps the client is out-of-sync client, so no-op"
          )
          logger.debug(`doNoActionForPlayer Timing.getUtcMsNow() ${Timing.getUtcMsNow()}`)
          logger.debug(`doNoActionForPlayer super.lastCheckpoint() ${super.lastCheckpoint()}`)
        }
      }
    } catch (error) {
      logger.error({ doNoActionForPlayerError: error })
    } finally {
      const eventVersion3 = this.updateState({ name: TableEventKind.DoNoActionForPlayerEvent })
      this.emitEvent({ ...TableEvents.createEvent(TableEventKind.DoNoActionForPlayerEvent, this), ...eventVersion3 })
      this.completeEvents()
    }
    return false
  }

  setPlayerDoNoActionCount(playerId: string) {
    logger.trace(`setPlayerDoNoActionCount ${playerId}`)
    const player = this.getPlayerById(playerId)
    player.doNoActionCount = 0
  }

  showCardsForTeacher() {
    logger.trace('showCardsForTeacher')
    this.isCardsVisibleByTeacher = true
    const eventVersion = this.updateState({ name: TableEventKind.ShowCardsForTeacherEvent }, false)
    this.emitEvent({ ...TableEvents.showCardsForTeacherEvent(this), ...eventVersion })

    this.completeEvents()
  }

  showCardsForEveryone() {
    logger.trace('showCardsForEveryone')
    this.isCardsVisibleByEveryone = true
    const eventVersion = this.updateState({ name: TableEventKind.ShowCardsForEveryoneEvent }, false)
    this.emitEvent({ ...TableEvents.showCardsForEveryoneEvent(this), ...eventVersion })
    this.completeEvents()
  }

  hideCards() {
    logger.trace('hideCards')
    this.isCardsVisibleByEveryone = false
    this.isCardsVisibleByTeacher = false
    this.players = this.players.map((player) => {
      player.isCardsVisibleByEveryone = false
      player.isCardsVisibleByTeacher = false

      return player
    })
    const eventVersion = this.updateState({ name: TableEventKind.HideCards }, false)
    this.emitEvent({ ...TableEvents.hideCardsEvent(this), ...eventVersion })

    this.completeEvents()
  }

  hidePlayerCards(playerId: string) {
    logger.trace(`hidePlayerCards ${playerId}`)
    const player = this.getPlayerById(playerId)
    player.hideCards()
    const eventVersion = this.updateState(
      {
        name: TableEventKind.HidePlayerCards,
        forPlayerId: playerId
      },
      false
    )
    this.emitEvent({ ...TableEvents.hidePlayerCardsEvent(this, player), ...eventVersion })
    this.completeEvents()
  }

  showPlayerCardsForTeacher(playerId: string) {
    logger.trace(`showPlayerCardsForTeacher ${playerId}`)
    const player = this.getPlayerById(playerId)
    player.showCardsForTeacher()
    const eventVersion = this.updateState(
      {
        name: TableEventKind.ShowPlayerCardsForTeacherEvent,
        forPlayerId: playerId
      },
      false
    )
    this.emitEvent({ ...TableEvents.showPlayerCardsForTeacherEvent(this, player), ...eventVersion })
    this.completeEvents()
  }

  showPlayerCardsForEveryone(playerId: string) {
    logger.trace(`showPlayerCardsForEveryone ${playerId}`)
    const player = this.getPlayerById(playerId)
    player.showCardsForEveryone()
    const eventVersion = this.updateState(
      {
        name: TableEventKind.ShowPlayerCardsForEveryoneEvent,
        forPlayerId: playerId
      },
      false
    )
    this.emitEvent({ ...TableEvents.showPlayerCardsForEveryoneEvent(this, player), ...eventVersion })
    this.completeEvents()
  }

  sitoutPlayerByTeacher(playerId: string) {
    logger.trace(`sitoutPlayerByTeacher ${playerId}`)
    const player = this.getPlayerById(playerId)
    player.foreverOut()
    const eventVersion = this.updateState({ name: TableEventKind.PlayerForeverOut, forPlayerId: playerId })
    this.emitEvent({ ...TableEvents.playerForeverOutEvent(this, player), ...eventVersion })
    if (this.isCurrentPlayer(playerId)) {
      this.nextTurn()
    }
  }

  playerInByTeacher(playerId: string) {
    logger.trace(`playerInByTeacher ${playerId}`)
    const player = this.getPlayerById(playerId)
    player.wait()
    const eventVersion = this.updateState({ name: TableEventKind.PlayerWaitlisted, forPlayerId: playerId })
    this.emitEvent({ ...TableEvents.playerWaitlistedEvent(this, player), ...eventVersion })
    if (this.isGameIdle()) {
      this.newHand()
    } else if (this.isCurrentPlayer(playerId) && this.playingPlayers().length === this.minPlayers) {
      this.nextTurn()
    } else {
      this.completeEvents()
    }
  }

  isGameIdle() {
    return this.playingPlayers().length === 0 || (this.isLastPlayerStanding() && this.playingPlayers().length <= 1)
  }

  isLastPlayerStanding() {
    return this.gamePlayers().length - this.outPlayers().length - this.foreverOutPlayers().length > 1
  }

  setColorForPlayer(playerId: string) {
    logger.trace(`setColorForPlayer ${playerId}`)
    const player = this.getPlayerById(playerId)
    // if (player.color === 'Brand/Cream') {
    //   // player.color = ColorsForPlayers[Math.floor(Math.random() * ColorsForPlayers.length)]
    //   player.color = Colors[Math.floor(Math.random() * Colors.length)]
    // }
    if (player.color !== 'Brand/Cream') {
      return
    }

    for (let i = 0; i < PlayerColors.length; i++) {
      const color = PlayerColors[i]
      const checkColor = this.players.find((p) => p.color === color && p.getId() !== player.getId())
      if (!checkColor) {
        player.color = color
        break
      }
    }
  }

  setTournamentBlinds() {
    logger.trace('setTournamentBlinds')
    this.bigBlind = TABLE_CONFIG.TOURNAMENT_BIG_BLIND
    this.blinds = new Blind(this.bigBlind)
    this.nextBlinds = new Blind(this.bigBlind * 2)
    this.mode = GameModes.Tournament
  }

  calculateNextBlinds() {
    logger.trace('calculateNextBlinds')
    if (GameStates.isFinished(this.state)) {
      this.completeEvents()
      return
    }

    const currentIndex = TABLE_CONFIG.BLIND_LEVELS.indexOf(this.updatedCurrentBlinds.big)

    // once we have increased the blinds to the max, stop at the top
    if (currentIndex >= TABLE_CONFIG.BLIND_LEVELS.length - 1) {
      // once we have increased the blinds to the max, stop at the top
    } else if (currentIndex !== TABLE_CONFIG.BLIND_LEVELS.length - 2) {
      // This check is not working because of changes in mode type
      // Please recheck this PR ( https://github.com/Poker-Powher/poker-power/pull/1511 )
      // if (GameModes.isTournament(this.mode) && currentIndex !== TABLE_CONFIG.BLIND_LEVELS.length - 2) {
      this.updatedCurrentBlinds.big = this.nextBlinds.big
      this.updatedCurrentBlinds.small = this.nextBlinds.small

      const next = this.fetchNextBlinds()

      this.nextBlinds.big = next.big
      this.nextBlinds.small = next.small

      let nextIndex = TABLE_CONFIG.BLIND_LEVELS.indexOf(next.big)
      this.nextBlindIndex = nextIndex

      nextIndex = nextIndex + 2
      this.blindTimerCheckpoint = this.blindTimerCheckpoint + nextIndex * this.blindTimerCountdown * 60000
    } else {
      this.updatedCurrentBlinds.big = this.nextBlinds.big
      this.updatedCurrentBlinds.small = this.nextBlinds.small

      this.nextBlinds.big = 0
      this.nextBlinds.small = 0
    }

    const eventVersion = this.updateState({ name: TableEventKind.CalculateNextBlindEvent })
    this.emitEvent({ ...TableEvents.calculateNextBlindEvent(this, { ...eventVersion }) })

    this.completeEvents()
  }

  fetchNextBlinds() {
    logger.trace('fetchNextBlinds')
    let nextBigBlinds = TABLE_CONFIG.BLIND_LEVELS[0]
    if (this.updatedCurrentBlinds.big >= nextBigBlinds) {
      const nextIndex = TABLE_CONFIG.BLIND_LEVELS.indexOf(this.updatedCurrentBlinds.big)
      nextBigBlinds = TABLE_CONFIG.BLIND_LEVELS[nextIndex + 1]
    }
    return { big: nextBigBlinds, small: nextBigBlinds / 2 }
  }

  playerRemovedByTeacher(playerId: string) {
    const player = this.getPlayerById(playerId)
    this.removedPlayerId = playerId
    const eventVersion = this.updateState({
      name: TableEventKind.PlayerRemovedByTeacherEvent,
      forPlayerId: playerId
    })
    this.emitEvent({ ...TableEvents.playerRemovedByTeacherEvent(this, player), ...eventVersion })
    //TODO: doLeaveGameForPlayer is being called in the processor, check if this is necessary
    this.doLeaveGameForPlayer(playerId)
    this.completeEvents()
  }

  setPlayerBuyIn(playerId: string) {
    logger.trace(`setPlayerBuyIn ${playerId}`)
    const player = this.getPlayerById(playerId)
    player.setBuyIn()
    const eventVersion = this.updateState({ name: TableEventKind.SetPlayerBuyInEvent, forPlayerId: playerId })
    this.emitEvent({ ...TableEvents.setPlayerBuyInEvent(this, player), ...eventVersion })
    this.completeEvents()
  }
}
